import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, ChevronDown, ChevronRight, MapPin, Clock, Gift, Users, Award, Heart, Sparkles, Droplets, MessageCircle, MessageSquare, Tag, Star, Video, Facebook, Instagram, ExternalLink, Home, Scissors, CalendarCheck, Phone, BookOpen, Lightbulb, Wand2, User, Crown, Camera, Map, HelpCircle, Zap, Shield, Globe, Tv, Briefcase, ArrowRight, CreditCard, LayoutGrid, Syringe, Flower2, Flame, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import SearchModal from "@/components/SearchModal";
import { KarineAudioButton } from "@/components/KarineAudioGreeting";
import useClientPoints from "@/hooks/useClientPoints";
import ClientLogoutLink from "@/components/ClientLogoutLink";
import { playMilestoneChord, isMilestoneSoundEnabled, setMilestoneSoundEnabled } from "@/utils/milestoneChord";
import { burstFromEl, showMilestoneToast } from "@/utils/milestoneCelebration";
import axios from "axios";
import { openContactModal } from "@/components/ContactModal";
import { CONCERNS } from "@/data/treatmentTaxonomy";
import { trackTreatmentView } from "@/utils/treatmentTracking";
import MobileTreatmentsSheet from "@/components/MobileTreatmentsSheet";
import { TreatmentPhotoGrid } from "@/components/menu/TreatmentPhotoGrid";

const Navbar = ({ scrollToSection: rawScrollToSection, openBookingModal: rawOpenBookingModal, openVirtualConsultation: rawOpenVirtualConsultation, openWaitlist: rawOpenWaitlist }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  // Signed-in client's Crown Points balance — inlined into the My
  // Account pill (Feb 24 2026) so we don't stack two overlapping
  // pills over the plaque.
  const { points: clientPoints, credit: clientCredit, nextTier: clientNextTier, pointsToNext: clientPointsToNext, bestTierNow: clientBestTierNow } = useClientPoints();
  const [pillHover, setPillHover] = useState(false);
  // Tier Milestone Pulse (Feb 24 2026): the first time a signed-in
  // client's balance crosses a redemption threshold we flash a gold
  // glow around the My Account pill. Persist the highest points
  // milestone we've celebrated in localStorage so it fires once and
  // never re-plays on refresh. Handles both fresh-load celebrations
  // and mid-session crossings (e.g. after a check-in that awards
  // points and the pill re-fetches).
  const [milestonePulse, setMilestonePulse] = useState(false);
  // Ref → the pill anchor so the confetti burst can spray FROM that
  // spot (not from a hardcoded corner). Falls back gracefully when null.
  const pillRef = useRef(null);
  // Client's opt-in preference for the celebratory chord (Feb 24 2026).
  // Muted by default; toggle lives inside the tier-progress tooltip.
  const [milestoneSound, setMilestoneSoundState] = useState(false);
  useEffect(() => {
    // Read the persisted preference once on mount so the toggle reflects
    // the client's choice across page refreshes.
    setMilestoneSoundState(isMilestoneSoundEnabled());
  }, []);
  useEffect(() => {
    if (clientPoints === null || !clientBestTierNow) return;
    const bestPts = Number(clientBestTierNow.points || 0);
    if (!bestPts) return;
    try {
      const KEY = "kl_last_pulse_tier_pts";
      const stored = Number(localStorage.getItem(KEY) || "0");
      if (bestPts > stored) {
        setMilestonePulse(true);
        localStorage.setItem(KEY, String(bestPts));
        // Fire the "cha-ching" chord if the client has opted in. The
        // helper is a no-op when the preference is off or Web Audio is
        // unavailable, so it's safe to call unconditionally.
        playMilestoneChord();
        // Champagne confetti burst from the pill's on-screen position
        // + luxe toast — the "full moment" trio (Feb 24 2026 owner ask).
        // Small delay so the confetti reads as a reward for the pulse
        // starting, not competing with it.
        setTimeout(() => {
          try { burstFromEl(pillRef.current); } catch { /* silent */ }
          try { showMilestoneToast(clientBestTierNow, { language }); } catch { /* silent */ }
        }, 180);
        // Auto-clear after the CSS animation completes so we don't
        // block hover interactions on the pill.
        const t = setTimeout(() => setMilestonePulse(false), 3200);
        return () => clearTimeout(t);
      }
    } catch { /* silent — localStorage disabled */ }
  }, [clientPoints, clientBestTierNow]);

  // Check if we're on the Skin Boosters page (hide bottom nav there - page has its own)
  const isOnSkinBoostersPage = location.pathname === '/skin-boosters';

  // Safe scroll — navigates to homepage if on a sub-page
  const scrollToSection = (sectionId) => {
    if (typeof rawScrollToSection === "function" && window.location.pathname === "/") {
      rawScrollToSection(sectionId);
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  // Safe modal openers — navigate to homepage if not available
  const openBookingModal = typeof rawOpenBookingModal === "function"
    ? rawOpenBookingModal
    : () => { window.location.href = "/#hero"; };

  const openVirtualConsultation = typeof rawOpenVirtualConsultation === "function"
    ? rawOpenVirtualConsultation
    : () => { window.location.href = "/?virtual=true"; };

  const openWaitlist = typeof rawOpenWaitlist === "function"
    ? rawOpenWaitlist
    : () => { window.location.href = "/?waitlist=true"; };
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [crownTooltipOpen, setCrownTooltipOpen] = useState(false);
  const [crownPoints, setCrownPoints] = useState(null); // null=unknown, number=balance
  const [navBottomY, setNavBottomY] = useState(220);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);
  const [mobileFinancingOpen, setMobileFinancingOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navBannerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const { cartCount, setIsCartOpen } = useCart();

  // Track the bottom-Y of the nav banner so the fixed-position mega-menu can
  // anchor itself directly under it (instead of being clipped by overflow).
  useEffect(() => {
    // ── Once-per-session attention pulse on the Treatments tab.
    //    Adds `nav-pulse-active` to <html> for ~2s then strips it so the
    //    animation never replays mid-session. Stored as sessionStorage so
    //    a returning visitor in the SAME tab doesn't see it twice, but a
    //    new visit (new tab / new browser launch) does. (Karine, 2026-06-30)
    try {
      if (!sessionStorage.getItem("kl_nav_pulse_shown")) {
        document.documentElement.classList.add("nav-pulse-active");
        sessionStorage.setItem("kl_nav_pulse_shown", "1");
        setTimeout(() => {
          document.documentElement.classList.remove("nav-pulse-active");
        }, 2200);
      }
    } catch { /* sessionStorage blocked (private mode) — skip silently */ }

    function measure() {
      if (navBannerRef.current) {
        const r = navBannerRef.current.getBoundingClientRect();
        setNavBottomY(r.bottom);
      }
    }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, []);

  // Hover helpers — keep the mega-menu open while the cursor briefly crosses
  // the gap between the nav button and the dropdown panel. Without this the
  // menu snaps shut the moment you try to reach for it.
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = (delay = 220) => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      closeTimerRef.current = null;
    }, delay);
  };
  const openOnHover = (dropdownType) => {
    cancelClose();
    setOpenDropdown(dropdownType);
  };

  // Live points balance for the Crown bottom-nav button (mobile only).
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    async function load() {
      const tok = localStorage.getItem("client_token");
      if (!tok) { setCrownPoints(null); return; }
      try {
        const r = await axios.get(`${API}/client/points-summary`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!cancelled) setCrownPoints(Number(r.data?.points_available ?? 0));
      } catch {
        // Defensive: do NOT auto-clear the token on transient errors.
        // Just leave the badge in its previous state.
        if (!cancelled) setCrownPoints(null);
      }
    }
    load();
    const sync = () => load();
    window.addEventListener("storage", sync);
    window.addEventListener("kl-auth-change", sync);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", sync);
      window.removeEventListener("kl-auth-change", sync);
    };
  }, []);

  // Crown rewards auto-tooltip — pulses every 45s for 3s on mobile so users
  // discover the loyalty programme without us being annoying about it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) return; // mobile only
    let pulseCount = 0;
    const MAX_PULSES = 3;
    const FIRST_DELAY = 18000;
    const REPEAT_INTERVAL = 45000;
    const VISIBLE_FOR = 3200;

    const showOnce = () => {
      if (pulseCount >= MAX_PULSES) return;
      setCrownTooltipOpen(true);
      pulseCount += 1;
      setTimeout(() => setCrownTooltipOpen(false), VISIBLE_FOR);
    };

    const initialTimer = setTimeout(showOnce, FIRST_DELAY);
    const repeatTimer = setInterval(showOnce, REPEAT_INTERVAL + FIRST_DELAY);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(repeatTimer);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Dropdown Data - with translations
  const promotionsItems = [
    // Oct 23 2026 · November Exclusive (Karine's poster) — Ottawa · Tanya
    { title: "8 Greens Facial · Nov Exclusive", titleFr: "Soin 8 Greens · Exclusivité novembre", description: language === "fr" ? "Éminence Équilibre hormonal · inclut la luminothérapie rouge et le double des points Crown Rewards · Ottawa" : "Éminence Hormonal Balance · includes red light therapy and double Crown Rewards points · Ottawa", price: "$129 + tax", highlight: true, badge: language === "fr" ? "NOVEMBRE" : "NOVEMBER" },
    { title: "Forehead + 11's Combo", titleFr: "Combo Front + 11's", description: t("dropdown.saveOnCombo"), price: "$349", original: "$448" },
    { title: "2 Syringes + FREE Tox", titleFr: "2 Seringues + Tox GRATUIT", description: t("dropdown.bestValueFiller"), price: "$950" },
    { title: "3 Syringes + FREE Tox", titleFr: "3 Seringues + Tox GRATUIT", description: t("dropdown.premiumFiller"), price: "$1,299" },
    { title: "Most Popular Package", titleFr: "Forfait Plus Populaire", description: "Forehead • Crow's Feet • 11's • Brow Lift • Smile Lift • Bunny Lines • Lip Flip", price: "$599", highlight: true, badge: language === "fr" ? "PLUS POPULAIRE" : "MOST POPULAR" },
    { title: "6 Syringes Package", titleFr: "Forfait 6 Seringues", description: t("dropdown.ultimateSavings"), price: "$2,199" },
  ];

  const serviceCategories = [
    {
      header: t("dropdown.neuromodulators"),
      items: [
        { name: language === "fr" ? "Les 3 Grandes Zones - Pattes d'Oie, 11's, Front" : "The Big 3 - Crow's Feet, 11's, Forehead", price: "$499" },
        { name: language === "fr" ? "Front" : "Forehead", price: "$199" },
        { name: language === "fr" ? "Numéro 11's" : "Number 11's", price: "$249" },
        { name: language === "fr" ? "Front + 11's (PROMO)" : "Forehead + 11's (PROMO)", price: "$349" },
        { name: language === "fr" ? "Pattes d'Oie" : "Crow's Feet", price: "$199" },
        { name: language === "fr" ? "Traitement Migraine" : "Migraine Treatment", price: "$950" },
        { name: language === "fr" ? "ATM/Grincement de Dents" : "TMJ/Jaw Grinding", price: "$549" },
        { name: "Lip Flip", price: "$125" },
      ]
    },
    {
      header: t("dropdown.dermalFillers"),
      items: [
        { name: language === "fr" ? "Augmentation Lèvres - Complet" : "Lip Enhancement - Full", price: "$499" },
        { name: language === "fr" ? "Augmentation Lèvres - Demi" : "Lip Enhancement - Half", price: "$299" },
        { name: language === "fr" ? "Joues" : "Cheeks", price: "$499" },
        { name: language === "fr" ? "Mâchoire" : "Jawline", price: "$499" },
        { name: language === "fr" ? "Menton" : "Chin", price: "$499" },
        { name: language === "fr" ? "Plis Nasolabiaux" : "Nasolabial Folds", price: "$499" },
        { name: language === "fr" ? "2 Seringues + Tox GRATUIT" : "2 Syringes + FREE Tox", price: "$950" },
      ]
    },
    {
      header: language === "fr" ? "Traitements Avancés" : "Advanced Treatments",
      items: [
        { name: language === "fr" ? "Sculptra Bio Stimulateur" : "Sculptra Bio Stimulator", price: language === "fr" ? "À partir de $799" : "From $799" },
        { name: t("dropdown.microNeedling"), price: language === "fr" ? "À partir de $350" : "From $350" },
        { name: t("dropdown.prpTherapy"), price: language === "fr" ? "À partir de $699" : "From $699" },
        { name: language === "fr" ? "Boosters Cutanés" : "Skin Boosters", price: language === "fr" ? "À partir de $450" : "From $450" },
        { name: language === "fr" ? "Consultation Gratuite" : "Free Consultation", price: language === "fr" ? "Gratuit" : "Free" },
      ]
    }
  ];

  // Botox treatments for dedicated dropdown - All 24 services
  const botoxTreatments = [
    // Popular & Core Treatments
    { name: language === "fr" ? "Les 3 Grandes Zones" : "The Big 3", description: language === "fr" ? "Pattes d'Oie, 11's, Front" : "Crow's Feet, 11's, Forehead", price: "$499", highlight: true, badge: language === "fr" ? "POPULAIRE" : "POPULAR" },
    { name: language === "fr" ? "Front" : "Forehead", price: "$199" },
    { name: language === "fr" ? "Numéro 11's" : "Number 11's", price: "$249" },
    { name: language === "fr" ? "Pattes d'Oie" : "Crow's Feet", price: "$299" },
    { name: language === "fr" ? "Front + 11's" : "Forehead + 11's", price: "$349", badge: "PROMO" },
    // Therapeutic
    { name: language === "fr" ? "Traitement Migraine" : "Migraine Treatment", price: "$950" },
    { name: language === "fr" ? "ATM/Grincement de Dents" : "TMJ/Jaw Grinding", price: "$549" },
    { name: language === "fr" ? "Hyperhidrose" : "Hyperhidrosis", price: "$799" },
    // Face & Neck
    { name: language === "fr" ? "Bandes du Cou" : "Neck Bands Platysmal", price: "$279" },
    { name: language === "fr" ? "Lifting Nefertiti" : "Nefertiti Jaw & Neck Lift", price: "$479" },
    { name: language === "fr" ? "Levée du Sourire" : "Smile Lift", price: "$125" },
    { name: language === "fr" ? "Sourire Gingival" : "Gummy Smile", price: "$125" },
    { name: "Lip Flip", price: "$125" },
    { name: language === "fr" ? "Rouleau Gelée (Sous les Yeux)" : "Jelly Roll (Under Eyes)", price: "$125" },
    { name: "MicroTox Neck", price: "$399" },
    { name: language === "fr" ? "MicroTox Visage Complet + Cou" : "MicroTox Full Face & Neck", price: "$1,110" },
    { name: language === "fr" ? "Trapèzes (Traptox)" : "Trapezoids (Traptox)", price: "$279" },
    { name: language === "fr" ? "Levée des Sourcils" : "Brow Lift", price: "$79", badge: "PROMO" },
    { name: language === "fr" ? "Lignes du Lapin" : "Bunny Lines", price: "$125" },
    // Packages
    { name: language === "fr" ? "Forfait Visage Supérieur" : "Full Upper Face Package", price: "$599", highlight: true },
    { name: language === "fr" ? "11's + Levée Sourcils ou Lip Flip" : "11's + Brow Lift or Lip Flip", price: "$299", badge: "PROMO" },
    { name: language === "fr" ? "Mini Rafraîchissement" : "Summer Refresh 2 — Mini", price: "$399", badge: "PROMO" },
    { name: language === "fr" ? "Le Plus Populaire" : "Summer Refresh 4 — Most Popular", price: "$599", badge: "PROMO", highlight: true },
    { name: language === "fr" ? "Visage Total" : "Summer Refresh 5 — Total Face", price: "$799", badge: "PROMO" },
  ];

  // Dermal fillers for dedicated dropdown - All 11 services
  const fillerTreatments = [
    // Lips
    { name: language === "fr" ? "Lèvres Luxueuses - Seringue Complète" : "Luscious Lip - Full Syringe", price: "$499", highlight: true },
    { name: language === "fr" ? "Lèvres Radiantes - 1/2 Seringue" : "Radiant Lip - 1/2 Syringe", price: "$299" },
    { name: language === "fr" ? "1 Seringue - Toute Zone" : "1 Full Syringe Any Area", price: "$499" },
    // Value Packages
    { name: language === "fr" ? "2 Seringues + Tox GRATUIT" : "2 Syringes + FREE Tox", price: "$950", highlight: true, badge: language === "fr" ? "POPULAIRE" : "POPULAR" },
    { name: language === "fr" ? "3 Seringues + Tox GRATUIT" : "3 Syringes + FREE Tox", price: "$1,299" },
    { name: language === "fr" ? "4 Seringues" : "4 Syringes", price: "$1,599" },
    { name: language === "fr" ? "5 Seringues" : "5 Full Syringes", price: "$1,899" },
    { name: language === "fr" ? "6 Seringues" : "6 Syringes", price: "$2,199", badge: language === "fr" ? "MEILLEUR PRIX" : "BEST PRICING" },
    { name: language === "fr" ? "8 Seringues" : "8 Syringes", price: "$2,899" },
    { name: language === "fr" ? "10 Seringues" : "10 Syringes", price: "$3,500", badge: language === "fr" ? "ACHAT EN GROS" : "BULK DEAL" },
    // Specialty
    { name: language === "fr" ? "Lignes de Fumeur" : "Smokers Lines", description: language === "fr" ? "2 Seringues + 16 Unités Neurotoxine" : "2 Syringes + 16 Units Neurotoxin", price: "$899" },
  ];

  // SEO Landing Pages for navigation
  const seoPages = [
    { name: language === "fr" ? "Botox à Ottawa" : "Botox in Ottawa", href: "/botox-ottawa", description: language === "fr" ? "Guide complet du Botox" : "Complete Botox guide" },
    { name: language === "fr" ? "Fillers Dermiques" : "Dermal Fillers", href: "/dermal-fillers", description: language === "fr" ? "Guide complet des fillers" : "Complete filler guide", featured: true },
    { name: language === "fr" ? "Remplissage Lèvres Ottawa" : "Lip Filler Ottawa", href: "/lip-filler-ottawa", description: language === "fr" ? "Augmentation des lèvres" : "Lip enhancement" },
    { name: language === "fr" ? "Remplissage Dermique Ottawa" : "Dermal Filler Ottawa", href: "/dermal-filler-ottawa", description: language === "fr" ? "Restauration du volume" : "Volume restoration" },
    { name: language === "fr" ? "Microneedling Médical" : "Medical Microneedling", href: "/medical-microneedling", description: language === "fr" ? "PRP, Exosomes, VAMP & plus" : "PRP, Exosomes, VAMP & more", featured: true },
    { name: language === "fr" ? "RF Microneedling" : "RF Microneedling", href: "/laser-and-skin#rf-microneedling", description: language === "fr" ? "Raffermissement & cicatrices · Ottawa avec une esthéticienne médicale" : "Skin tightening & scars · Ottawa with a Medical Aesthetician", featured: true },
    { name: language === "fr" ? "Microneedling Ottawa" : "Microneedling Ottawa", href: "/microneedling-ottawa", description: language === "fr" ? "Rajeunissement cutané" : "Skin rejuvenation" },
    { name: language === "fr" ? "PRP Ottawa" : "PRP Ottawa", href: "/prp-ottawa", description: language === "fr" ? "Plasma riche en plaquettes" : "Platelet-rich plasma" },
    { name: language === "fr" ? "Sculptra Ottawa" : "Sculptra Ottawa", href: "/sculptra-ottawa", description: language === "fr" ? "Bio-stimulateur de collagène" : "Collagen bio-stimulator" },
    { name: language === "fr" ? "Radiesse® · CaHA" : "Radiesse® · CaHA", href: "/radiesse", description: language === "fr" ? "Lift immédiat + collagène · mains et visage" : "Immediate lift + collagen · face & hands", featured: true },
  ];

  const aboutItems = [
    { icon: Award, title: t("dropdown.experience"), description: language === "fr" ? "Expertise clinique depuis 1989" : "Clinical nursing expertise since 1989" },
    { icon: Heart, title: language === "fr" ? "Rencontrez Karine" : "Meet Karine", description: language === "fr" ? "Votre injectrice de précision dévouée" : "Your dedicated precision injectionist", href: "/about" },
    { icon: Sparkles, title: language === "fr" ? "Nos esthéticiennes médicales" : "Our Medical Aestheticians", description: language === "fr" ? "Esthéticiennes médicales — Laser & HydraFacial" : "Medical Aestheticians — Laser & HydraFacial", href: "/team" },
    { icon: Users, title: language === "fr" ? "Rencontrez l'Équipe" : "Meet the Team", description: language === "fr" ? "Karine et toute l'équipe" : "Karine & the whole team", href: "/team" },
    { icon: Star, title: language === "fr" ? "100+ Avis 5 Étoiles" : "100+ 5-Star Reviews", description: language === "fr" ? "Faites confiance à des centaines de clients" : "Trusted by hundreds of clients" },
    { icon: Sparkles, title: language === "fr" ? "Avant & Après" : "Before & After", description: language === "fr" ? "Voir les résultats de nos traitements" : "See our treatment results", href: "/before-after" },
    { icon: Award, title: t("dropdown.aclsCertified"), description: language === "fr" ? "Certifiée en support vital avancé" : "Advanced cardiac life support certified" },
    { icon: HelpCircle, title: "FAQ", description: language === "fr" ? "Questions fréquemment posées" : "Frequently asked questions", href: "/faq" },
    { icon: MapPin, title: language === "fr" ? "Nos Emplacements" : "Our Locations", description: language === "fr" ? "Ottawa, Carleton Place, Kanata" : "Ottawa, Carleton Place, Kanata", scrollTo: "locations" },
    { icon: Phone, title: language === "fr" ? "Contactez-Nous" : "Contact Us", description: language === "fr" ? "Envoyez-nous un message" : "Send us a message", scrollTo: "contact" },
    { icon: Briefcase, title: language === "fr" ? "Carrières" : "Careers", description: language === "fr" ? "Rejoignez notre équipe" : "Join our team", href: language === "fr" ? "/fr/carrieres" : "/careers", badge: language === "fr" ? "On embauche" : "Hiring" },
    { icon: Syringe, title: language === "fr" ? "Joignez-vous à notre équipe" : "Join Our Team", description: language === "fr" ? "Injecteurs esthétiques — MD · IP · IA" : "Cosmetic injectors — MD · NP · RN", href: language === "fr" ? "/fr/joignez-notre-equipe" : "/join-our-team", badge: language === "fr" ? "On embauche" : "Hiring" },
  ];

  const locationItems = [
    { 
      name: "Ottawa", 
      address: "1390 Prince of Wales Drive, Main Floor",
      city: "Ottawa, ON",
      hours: t("dropdown.byAppointment"),
      phone: "(613) 850-0805",
      bookingUrl: "https://karine-lauzon-rn-ottawa.square.site/"
    },
    { 
      name: "Carleton Place", 
      address: "163 Bridge Street, Downtown",
      city: "Carleton Place, ON",
      hours: language === "fr" ? "Lun-Ven: 10h-18h, Sam: 9h-13h" : "Mon-Fri: 10AM-6PM, Sat: 9AM-1PM",
      phone: "(613) 850-0805",
      bookingUrl: "https://tinyurl.com/Book-Carleton-Place"
    },
    { 
      name: "Kanata", 
      address: "320 March Road, Sixth Floor, Clinic 603",
      city: "Kanata, ON",
      hours: t("dropdown.byAppointment"),
      phone: "(613) 850-0805",
      bookingUrl: "https://karine-lauzon-rn.square.site/"
    },
  ];

  const aftercareItems = [
    { icon: Sparkles, title: t("dropdown.botoxAftercare"), description: t("dropdown.botoxDesc"), color: "bg-blue-50 text-blue-600" },
    { icon: Droplets, title: t("dropdown.fillerAftercare"), description: t("dropdown.fillerDesc"), color: "bg-pink-50 text-pink-600" },
    { icon: Clock, title: language === "fr" ? "Calendrier de Récupération" : "Recovery Timeline", description: language === "fr" ? "À quoi s'attendre jour après jour" : "What to expect day by day", color: "bg-amber-50 text-amber-600" },
    { icon: Award, title: language === "fr" ? "Soins de Suivi" : "Follow-Up Care", description: language === "fr" ? "Quand planifier votre prochaine visite" : "When to schedule your next visit", color: "bg-green-50 text-green-600" },
  ];

  const giftCardItems = [
    { title: t("dropdown.purchaseGiftCard"), description: t("dropdown.giveGiftOfBeauty"), url: "https://app.squareup.com/gift/ML3WE3AGAHJWN/order", highlight: true },
    { title: t("dropdown.anyAmount"), description: t("dropdown.chooseValue") },
    { title: t("dropdown.validAllLocations"), description: t("dropdown.allThreeLocations") },
    { title: t("dropdown.noExpiration"), description: t("dropdown.noRush") },
    { title: t("dropdown.emailDelivery"), description: t("dropdown.instantDigital") },
  ];

  const financeItems = [
    { title: "Afterpay", description: t("dropdown.shopNowPayLater"), highlight: true },
    { title: language === "fr" ? "0% Intérêt" : "0% Interest", description: t("dropdown.noExtraFees") },
    { title: language === "fr" ? "Approbation Facile" : "Easy Approval", description: t("dropdown.quickApproval") },
    { title: language === "fr" ? "Paiements Flexibles" : "Flexible Payments", description: language === "fr" ? "Divisez vos coûts de traitement" : "Split your treatment costs" },
  ];

  const chatItems = [
    { icon: MessageCircle, title: t("dropdown.facebookMessenger"), description: t("dropdown.chatInstantly"), url: "https://m.me/PreciseRNInjectionist", color: "bg-[#0084FF]" },
    { icon: MessageSquare, title: t("dropdown.textUsAt"), description: "(613) 850-0805", url: "sms:613-850-0805?body=Hi, I'd like to book an appointment", color: "bg-green-500" },
    { icon: Video, title: language === "fr" ? "Guide Consultation Virtuelle" : "Virtual Consultation Guide", description: language === "fr" ? "Comment utiliser les appels vidéo" : "How to use video calls", href: "/virtual-consultation-guide", color: "bg-cyan-500" },
    { icon: Clock, title: t("dropdown.responseTime"), description: t("dropdown.usuallyWithin") },
  ];

  const beautyHubItems = [
    { icon: Sparkles, title: language === "fr" ? "Botox & Neuromodulateurs" : "Botox & Neuromodulators", description: language === "fr" ? "Tout sur le Botox et les zones traitées" : "Everything about Botox and treatment areas", href: "/aesthetic-guide", tab: "botox", color: "bg-indigo-100 text-indigo-600" },
    { icon: Droplets, title: language === "fr" ? "Fillers Dermiques" : "Dermal Fillers", description: language === "fr" ? "Volume, contours et techniques" : "Volume, contours and techniques", href: "/aesthetic-guide", tab: "fillers", color: "bg-purple-100 text-purple-600" },
    { icon: Star, title: language === "fr" ? "Sculptra & Collagène" : "Sculptra & Collagen", description: language === "fr" ? "Bio-stimulateurs et résultats durables" : "Bio-stimulators and lasting results", href: "/aesthetic-guide", tab: "sculptra", color: "bg-amber-100 text-amber-600" },
    { icon: Heart, title: language === "fr" ? "Baby Glow & Qualité Peau" : "Skin Quality & Baby Glow", description: language === "fr" ? "Redensity 1 et hydratation profonde" : "Redensity 1 and deep hydration", href: "/aesthetic-guide", tab: "babyglow", color: "bg-teal-100 text-teal-600" },
    { icon: Wand2, title: language === "fr" ? "Combinaisons de Traitements" : "Treatment Combinations", description: language === "fr" ? "Les meilleurs combos par Karine" : "Best combos recommended by Karine", href: "/aesthetic-guide", tab: "combos", color: "bg-rose-100 text-rose-600" },
    { icon: BookOpen, title: language === "fr" ? "Questions Fréquentes" : "Frequently Asked Questions", description: language === "fr" ? "Réponses aux questions fréquentes" : "Answers to common patient questions", href: "/aesthetic-guide", tab: "faq", color: "bg-zinc-100 text-zinc-600" },
    { icon: Camera, title: language === "fr" ? "Analyse IA Peau" : "AI Skin Analysis", description: language === "fr" ? "Analysez votre peau avec l'IA" : "Upload your photo for AI analysis", href: "/?analysis=true", color: "bg-orange-100 text-orange-600" },
    { icon: Lightbulb, title: language === "fr" ? "Conseil du Jour" : "Karine's 1 Minute Skin Tip", description: language === "fr" ? "Haute Education • Astuces quotidiennes" : "Higher Education • Daily tips from Karine", href: "/aesthetic-guide", tab: "tip", color: "bg-yellow-100 text-yellow-600" },
    { icon: Briefcase, title: language === "fr" ? "Programme Pro Partenaires" : "Pro Partner Program", description: language === "fr" ? "Pros : référez et gagnez 100 $ par client référé" : "Pros: refer & earn $100 per client referral", href: "/professionals", color: "bg-amber-100 text-amber-700" },
  ];

  const navLinks = [
    { label: t("nav.promotions"), id: "promotions", hasDropdown: true, dropdownType: "promotions", href: "/promotions", mobileHref: "/promotions", isFeaturedPromotions: true },
    { label: language === "fr" ? "Traitements" : "Treatments", id: "treatments", hasDropdown: true, dropdownType: "megaMenuTreatments", href: "/treatments", mobileHref: "/treatments", isFeatured: true },
    // Sept 2026 · Karine ask · one-tap link to /treatments/all so the
    // complete menu (every service · every price) is one click from
    // every page.
    // Sep 28 2026 · Karine ask · "improve visibility of Promotions,
    // Treatments and Full Menu buttons". Flagged as isFullMenu so the
    // renderer promotes it to a navy outline pill matching the trio.
    { label: language === "fr" ? "Menu complet" : "Full Menu", id: "full-menu", hasDropdown: false, href: "/book/menu", mobileHref: "/book/menu", isFullMenu: true },
    // Oct 22 2026 · Karine ask — "Book FREE Consultation" 4th from the left,
    // stands out, opens the provider picker page.
    { label: language === "fr" ? "Consultation GRATUITE" : "Book FREE Consultation", shortLabel: language === "fr" ? "GRATUITE" : "FREE Consult", id: "free-consult", hasDropdown: false, href: "/book/free-consultation", mobileHref: "/book/free-consultation", isFreeConsult: true },
    // Oct 17 2026 · Karine ask — "Treatments by Provider" tab: Karine · Tanya · Linda, one-tap book.
    { label: language === "fr" ? "Soins par praticienne" : "Treatments by Provider", shortLabel: language === "fr" ? "Praticiennes" : "By Provider", id: "by-provider", href: "/treatments/by-provider", mobileHref: "/treatments/by-provider", isNew: true },
    // Oct 22 2026 · Karine ask — Prevention moved INTO the Treatments menu;
    // About folded INTO Meet the Team (dropdown) so the Virtual chip fits.
    { label: language === "fr" ? "Rencontrez l'Équipe" : "Meet the Team", shortLabel: language === "fr" ? "L'Équipe" : "Our Team", id: "team", hasDropdown: true, dropdownType: "about", href: "/team", mobileHref: "/team", isHiring: true },
    // Oct 17 2026 · Karine ask — Prior & After Care moved INTO the Skin
    // Tips dropdown (was its own tab) to make room for Treatments by Provider.
    { label: language === "fr" ? "Conseils Peau" : "Skin Tips", id: "skin-tips", hasDropdown: true, dropdownType: "skinTips", href: "/skincare-101", mobileHref: "/skincare-101", isSkinTips: true },
    { label: language === "fr" ? "Demandez Karine FAQ" : "Ask Karine FAQ", shortLabel: language === "fr" ? "FAQ" : "Ask Karine", id: "aesthetic-guide", hasDropdown: true, dropdownType: "beautyHub", href: "/aesthetic-guide", mobileHref: "/aesthetic-guide" },
    { label: t("nav.giftCards"), shortLabel: language === "fr" ? "Cadeaux" : "Gifts", id: "gift-certificates", href: "/gift-cards" },
    { label: language === "fr" ? "Acheter des soins" : "Buy Skincare", shortLabel: language === "fr" ? "Soins" : "Skincare", id: "shop", href: "/shop", isBrandGradient: true },
  ];

  // Oct 22 2026 · below xl (1280–1535 px) the row overflowed and hid the
  // Virtual chip. Long tabs get a short label until 2xl so everything fits.
  const navLabel = (link) => link.shortLabel
    ? (language === "fr"
        ? (<><span className="min-[2100px]:hidden">{link.shortLabel}</span><span className="hidden min-[2100px]:inline">{link.label}</span></>)
        : (<><span className="min-[1720px]:hidden">{link.shortLabel}</span><span className="hidden min-[1720px]:inline">{link.label}</span></>))
    : link.label;

  const handleNavClick = (link) => {
    // Navigate to the page if href is provided
    if (link.href) {
      window.location.href = link.href;
    } else if (window.location.pathname !== '/') {
      window.location.href = `/#${link.scrollTo || link.id}`;
    } else if (link.scrollTo) {
      scrollToSection(link.scrollTo);
    } else {
      scrollToSection(link.id);
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const mobileScrollOrNav = (sectionId) => {
    setIsMobileMenuOpen(false);
    setMobileOpenDropdown(null);
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      scrollToSection(sectionId);
    }
  };

  const handleDropdownToggle = (dropdownType, e) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === dropdownType ? null : dropdownType);
  };

  // ── Oct 2 2026 · Karine ask ──────────────────────────────────────────
  // "The drop down menu needs work and has to be much more noticeable
  //  and the set to navigate, should be very easy to see heading such
  //  as Botox, fillers and all other heading, you can use color as well."
  //

  const renderDropdown = (type) => {
    switch(type) {
      case 'megaMenuTreatments':
        // ── Redesigned mega-menu (Feb 28 2026 · Karine) ────────────────
        // Karine ask: "Facials & Body is below the fold — make it all
        // show above the fold." The old layout showed every item per
        // category (Facials & Body had 9 → made the column tall enough
        // to force a second grid row past the fold on ≤1366px).
        //
        // New rules:
        //   • Cap each category to 4 items (featured first, then rest)
        //   • 6 categories in a single row on ≥xl, 3-per-row on lg,
        //     2-per-row on smaller — so Facials & Body is always visible
        //   • Concerns dropped to a compact chip row in the bottom rail
        //   • Tight spacing (space-y-1 · text-[12.5px]) shrinks the
        //     vertical footprint by ~40 %.
        return (
          <div
            className="fixed left-1/2 -translate-x-1/2 z-[10001]"
            style={{ top: navBottomY }}
            onMouseEnter={cancelClose}
            onMouseMove={cancelClose}
            data-testid="mega-menu-treatments-wrapper"
          >
            <div
              className="w-[1240px] max-w-[98vw] bg-background rounded-2xl shadow-2xl border border-border animate-fade-in overflow-y-auto overscroll-contain"
              style={{ maxHeight: `calc(100vh - ${navBottomY + 16}px)` }}
              data-testid="mega-menu-treatments"
            >
            <div className="p-4 md:p-5">
              {/* Photo-card grid — Oct 10 2026 · Karine ask: the menu IS the photo cards */}
              <TreatmentPhotoGrid lang={language} onNavigate={() => setOpenDropdown(null)} cols="grid-cols-4 xl:grid-cols-8" compact testIdPrefix="mega-menu-card" />
            </div>

            {/* Bottom rail — Concerns chips + View-All link (compact, one row) */}
            <div className="px-4 md:px-5 py-3 bg-gradient-to-r from-amber-50/60 via-amber-50/40 to-zinc-50 border-t border-zinc-100 rounded-b-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800 pr-1">
                  <Sparkles className="w-3 h-3" />
                  {language === 'fr' ? 'Par Préoccupation' : 'Shop by Concern'}
                </span>
                {CONCERNS.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/concerns/${c.slug}`}
                    onClick={() => setOpenDropdown(null)}
                    className="text-[11.5px] font-medium text-zinc-700 hover:text-amber-800 hover:bg-white bg-white/60 border border-amber-100 rounded-full px-2.5 py-1 transition-colors"
                    data-testid={`mega-concern-${c.slug}`}
                  >
                    {c.label[language === 'fr' ? 'fr' : 'en']}
                  </Link>
                ))}
                <span className="mx-1 hidden md:inline text-zinc-300">·</span>
                <Link
                  to="/book/menu"
                  onClick={() => setOpenDropdown(null)}
                  className="inline-flex items-center gap-1 text-xs font-black text-emerald-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap transition-colors"
                  data-testid="mega-full-menu-link"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Menu Complet' : 'Full Studio Menu'}
                </Link>
                <Link
                  to="/prevention"
                  onClick={() => setOpenDropdown(null)}
                  className="inline-flex items-center gap-1 text-xs font-black text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-3 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap transition-colors"
                  data-testid="mega-prevention-link"
                >
                  <Leaf className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Prévention · Bien Vieillir' : 'Prevention · Age Well'}
                </Link>
                <Link
                  to="/treatments"
                  onClick={() => setOpenDropdown(null)}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-zinc-900 hover:text-amber-700 transition-colors whitespace-nowrap"
                  data-testid="mega-view-all-link"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Voir tous les traitements' : 'View All Treatments'}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
            </div>
          </div>
        );

      case 'promotions':
        return (
          <div 
            className="absolute top-full left-0 mt-2 w-[420px] bg-background rounded-lg shadow-2xl border border-border p-5 animate-fade-in z-[9999] overflow-y-auto overscroll-contain"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
            onMouseEnter={() => setOpenDropdown('promotions')}
          >
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
              <Tag className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">
                {t("dropdown.currentPromotions")}
              </h3>
            </div>
            <ul className="space-y-3">
              {promotionsItems.map((item, idx) => (
                <li key={idx} className="relative">
                  {item.badge && (
                    <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">
                      {item.badge}
                    </span>
                  )}
                  <button
                    onClick={() => { scrollToSection("promotions"); setOpenDropdown(null); }}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      item.highlight 
                        ? 'bg-gradient-to-r from-amber-100 to-yellow-50 border-2 border-amber-400 hover:from-amber-200 hover:to-yellow-100 shadow-md' 
                        : 'bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`font-medium ${item.highlight ? 'text-amber-900' : 'text-gray-900'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                          {language === "fr" ? item.titleFr : item.title}
                          {item.highlight && <Star className="inline w-4 h-4 ml-1 text-amber-500 fill-amber-500" />}
                        </p>
                        <p className={`text-xs mt-0.5 ${item.highlight ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>{item.description}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${item.highlight ? 'text-amber-600 text-lg' : 'text-red-600'}`}>{item.price}</p>
                        {item.original && <p className="text-xs text-gray-400 line-through">{item.original}</p>}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
              <button onClick={() => { scrollToSection("promotions"); setOpenDropdown(null); }} className="text-sm text-gray-600 hover:text-gray-800">
                {language === "fr" ? "Voir sur cette page" : "View on this page"} →
              </button>
              <a href="/promotions" className="text-sm text-red-600 hover:text-red-700 font-medium">
                {t("dropdown.viewAllPromotions")} →
              </a>
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="absolute top-full left-0 mt-2 w-[850px] max-w-[95vw] bg-background rounded-lg shadow-2xl border border-border p-6 animate-fade-in z-[9999]">
            <div className="grid grid-cols-4 gap-6">
              {serviceCategories.map((category) => (
                <div key={category.header}>
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider border-b border-gray-200 pb-2">
                    {category.header}
                  </h3>
                  <ul className="space-y-2">
                    {category.items.map((item) => (
                      <li key={item.name}>
                        <button
                          onClick={() => { scrollToSection("services"); setOpenDropdown(null); }}
                          className="text-left w-full text-sm text-gray-600 hover:text-black transition-colors group"
                        >
                          <span className="group-hover:underline">{item.name}</span>
                          <span className="block text-xs text-amber-700 font-medium">{item.price}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              
              {/* SEO Pages Column */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider border-b border-amber-300 pb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-amber-600" />
                  {language === "fr" ? "Guides & Info" : "Guides & Info"}
                </h3>
                <ul className="space-y-2">
                  {seoPages.map((page) => (
                    <li key={page.href}>
                      <Link
                        to={page.href}
                        onClick={() => setOpenDropdown(null)}
                        className="text-left w-full text-sm text-gray-600 hover:text-amber-700 transition-colors group block"
                      >
                        <span className="group-hover:underline font-medium">{page.name}</span>
                        <span className="block text-xs text-gray-400">{page.description}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
              <button onClick={() => { scrollToSection("services"); setOpenDropdown(null); }} className="text-sm text-black hover:text-gray-600 font-medium">
                {t("dropdown.viewAllServices")} →
              </button>
              <span className="text-xs text-gray-400">{t("dropdown.freeConsultations")}</span>
            </div>
          </div>
        );
      
      case 'skinTips':
        return (
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-background rounded-lg shadow-2xl border border-border animate-fade-in z-[9999] p-3"
            onMouseEnter={() => setOpenDropdown('skinTips')}
            data-testid="navbar-skin-tips-dropdown"
          >
            <ul className="space-y-1">
              {[
                { href: "/skincare-101", icon: Sparkles, title: language === "fr" ? "Bibliothèque Conseils Peau" : "Skin Tips Library", description: language === "fr" ? "50 articles · soins quotidiens, produits, routines" : "50 articles · daily skincare, products, routines", badge: "50" },
                { href: "/aftercare", icon: Heart, title: language === "fr" ? "Soins avant & après" : "Prior & After Care", description: language === "fr" ? "Quoi faire avant et après chaque traitement" : "What to do before and after every treatment" },
              ].map((item) => (
                <li key={item.href}>
                  <a href={item.href} onClick={() => setOpenDropdown(null)} className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors" data-testid={`navbar-skin-tips-item-${item.href.replace(/\//g, '')}`}>
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center"><item.icon className="w-5 h-5 text-primary" /></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                        {item.badge && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold">{item.badge}</span>}
                      </div>
                      <span className="block text-xs text-gray-500">{item.description}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'about':
        return (
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[380px] bg-background rounded-lg shadow-2xl border border-border animate-fade-in z-[9999] flex flex-col"
            style={{ maxHeight: 'min(520px, calc(100vh - 180px))' }}
            onMouseEnter={() => setOpenDropdown('about')}
          >
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
              <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider border-b border-gray-200 pb-2">
                {t("dropdown.aboutKarine")}
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5">
              <ul className="space-y-2 pb-4">
                {aboutItems.map((item, idx) => (
                  <li key={idx}>
                    {item.href ? (
                      <a
                        href={item.href}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                        data-testid={item.badge ? `navbar-about-item-${item.title.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                      >
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                            {item.badge && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-800 text-white text-[9px] uppercase tracking-widest font-bold">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <span className="block text-xs text-gray-500">{item.description}</span>
                        </div>
                      </a>
                    ) : item.scrollTo ? (
                      <button
                        onClick={() => { scrollToSection(item.scrollTo); setOpenDropdown(null); }}
                        className="flex items-center gap-3 w-full text-left hover:bg-amber-50 p-2 rounded-lg transition-colors"
                      >
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                          <span className="block text-xs text-gray-500">{item.description}</span>
                        </div>
                      </button>
                    ) : (
                      <a
                        href="/about"
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      >
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                          <span className="block text-xs text-gray-500">{item.description}</span>
                        </div>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100">
              <a href="/about" onClick={() => setOpenDropdown(null)} className="text-sm text-black hover:text-gray-600 font-medium">
                {t("dropdown.learnMore")} →
              </a>
            </div>
          </div>
        );
      
      case 'locations':
        return (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[500px] bg-background rounded-lg shadow-2xl border border-border p-5 animate-fade-in z-[9999]">
            <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider border-b border-gray-200 pb-2">
              {t("dropdown.ourLocations")}
            </h3>
            <ul className="space-y-3">
              {locationItems.map((location, idx) => (
                <li key={idx} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{location.name}</h4>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                        <MapPin className="w-4 h-4" />
                        {location.address}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                        <Clock className="w-4 h-4" />
                        {location.hours}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                        <MessageSquare className="w-4 h-4" />
                        {t("topbar.textUs")} {location.phone}
                      </div>
                    </div>
                    <a
                      href={location.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("nav.bookNow")}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <button onClick={() => { scrollToSection("locations"); setOpenDropdown(null); }} className="text-sm text-black hover:text-gray-600 font-medium">
                {t("misc.viewAll")} →
              </button>
            </div>
          </div>
        );
      
      case 'aftercare':
        return (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[380px] bg-background rounded-lg shadow-2xl border border-border p-5 animate-fade-in z-[9999]">
            <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider border-b border-gray-200 pb-2">
              {t("dropdown.aftercareGuides")}
            </h3>
            <ul className="space-y-2">
              {aftercareItems.map((item, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => { scrollToSection("aftercare"); setOpenDropdown(null); }}
                    className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-3 rounded-lg transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                      <span className="block text-xs text-gray-500">{item.description}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <button onClick={() => { scrollToSection("aftercare"); setOpenDropdown(null); }} className="text-sm text-black hover:text-gray-600 font-medium">
                {t("dropdown.viewFullGuide")} →
              </button>
            </div>
          </div>
        );
      
      case 'gift':
        return (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-gradient-to-br from-gray-900 to-black rounded-lg shadow-2xl border border-gray-700 p-5 animate-fade-in z-[9999]">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-700">
              <Gift className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-white text-sm uppercase tracking-wider">
                {t("dropdown.giftCertificates")}
              </h3>
            </div>
            <ul className="space-y-2">
              {giftCardItems.map((item, idx) => (
                <li key={idx}>
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${item.highlight ? 'bg-amber-500 hover:bg-amber-400' : 'hover:bg-white/10'}`}
                    >
                      <Gift className={`w-5 h-5 ${item.highlight ? 'text-black' : 'text-amber-400'}`} />
                      <div>
                        <span className={`text-sm font-medium ${item.highlight ? 'text-black' : 'text-white'}`}>{item.title}</span>
                        <span className={`block text-xs ${item.highlight ? 'text-black/70' : 'text-gray-400'}`}>{item.description}</span>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-center gap-3 p-2">
                      <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                      <div>
                        <span className="text-sm font-medium text-white">{item.title}</span>
                        <span className="block text-xs text-gray-400">{item.description}</span>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-gray-700">
              <button onClick={() => { scrollToSection("gift-certificates"); setOpenDropdown(null); }} className="text-sm text-amber-400 hover:text-amber-300 font-medium">
                {t("dropdown.learnMoreGiftCards")} →
              </button>
            </div>
          </div>
        );

      case 'beautyHub':
        return (
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-background rounded-lg shadow-2xl border border-border animate-fade-in z-[9999] flex flex-col"
            style={{ maxHeight: 'min(520px, calc(100vh - 180px))' }}
            onMouseEnter={() => setOpenDropdown('beautyHub')}
          >
            <div className="flex-shrink-0 px-4 pt-4 pb-2 border-b border-rose-200">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">
                    {language === "fr" ? "Demandez Karine FAQ" : "Ask Karine FAQ"}
                  </h3>
                  <p className="text-[10px] text-amber-600 font-medium tracking-wider uppercase">
                    {language === "fr" ? "Centre de Haute Education" : "Higher Education Center"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-2">
              <ul className="space-y-1">
                {beautyHubItems.map((item, idx) => (
                  <li key={idx}>
                    {item.action === "waitlist" ? (
                      <button
                        onClick={() => { setOpenDropdown(null); openWaitlist(); }}
                        className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
                          <item.icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 block" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                          <span className="block text-xs text-gray-500 truncate">{item.description}</span>
                        </div>
                      </button>
                    ) : (
                      <Link
                        to={`${item.href}${item.tab ? `?tab=${item.tab}` : ''}`}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
                          <item.icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 block" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                          <span className="block text-xs text-gray-500 truncate">{item.description}</span>
                        </div>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100">
              <Link to="/aesthetic-guide" onClick={() => setOpenDropdown(null)} className="text-sm text-rose-600 hover:text-rose-700 font-medium">
                {language === "fr" ? "Voir le Guide Complet" : "View Full Ask Karine FAQ"} →
              </Link>
            </div>
          </div>
        );

      case 'skinBoosters':
        return (
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[380px] bg-background rounded-lg shadow-2xl border border-border overflow-hidden animate-fade-in z-[9999] flex flex-col"
            style={{ maxHeight: 'min(600px, calc(100vh - 150px))' }}
            onMouseEnter={() => setOpenDropdown('skinBoosters')}
          >
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {language === "fr" ? "Skin Boosters & Combos" : "Skin Boosters & Combos"}
                  </span>
                </div>
                <span className="px-2 py-0.5 bg-white text-amber-600 text-[10px] font-bold rounded-full">
                  NEW!
                </span>
              </div>
              <p className="text-amber-100 text-xs mt-1">30 {language === "fr" ? "traitements Redensity 1" : "Redensity 1 treatments"}</p>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {/* Categories */}
              <div className="p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-medium">{language === "fr" ? "Catégories" : "Browse by Category"}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/skin-boosters?filter=boosters"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center gap-2 p-3 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                      <Droplets className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800 group-hover:text-teal-700">{language === "fr" ? "Boosters" : "Boosters"}</span>
                      <span className="block text-[10px] text-gray-500">15 {language === "fr" ? "traitements" : "treatments"}</span>
                    </div>
                  </Link>
                  <Link
                    to="/skin-boosters?filter=botox"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800 group-hover:text-purple-700">Botox</span>
                      <span className="block text-[10px] text-gray-500">3 {language === "fr" ? "traitements" : "treatments"}</span>
                    </div>
                  </Link>
                  <Link
                    to="/skin-boosters?filter=combos"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center gap-2 p-3 rounded-lg bg-gradient-to-r from-purple-50 to-teal-50 hover:from-purple-100 hover:to-teal-100 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-teal-500 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800 group-hover:text-purple-700">Combos</span>
                      <span className="block text-[10px] text-gray-500">9 {language === "fr" ? "forfaits" : "packages"}</span>
                    </div>
                  </Link>
                  <Link
                    to="/skin-boosters?filter=vip"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                      <Crown className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800 group-hover:text-amber-700">VIP</span>
                      <span className="block text-[10px] text-gray-500">3 {language === "fr" ? "forfaits premium" : "premium packages"}</span>
                    </div>
                  </Link>
                </div>
              </div>
              
              {/* Popular Picks */}
              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-medium">{language === "fr" ? "Populaires" : "Popular Picks"}</p>
                <div className="space-y-2">
                  <Link
                    to="/skin-boosters?highlight=1"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">#1</span>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{language === "fr" ? "Baby Glow Visage" : "The Baby Glow Infusion"}</span>
                      <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded bg-amber-100 text-amber-700">{language === "fr" ? "#1 RECOMMANDÉ" : "#1 RECOMMENDED"}</span>
                    </div>
                    <span className="text-sm font-bold text-amber-600">$699</span>
                  </Link>
                  <Link
                    to="/skin-boosters?highlight=2"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">#2</span>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>The Radiance Lift</span>
                      <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded bg-teal-100 text-teal-600">{language === "fr" ? "VISAGE + COU" : "FACE + NECK"}</span>
                    </div>
                    <span className="text-sm font-bold text-amber-600">$899</span>
                  </Link>
                  <Link
                    to="/skin-boosters?highlight=21"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">#21</span>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>The Big 3 Glow</span>
                      <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded bg-rose-100 text-rose-600">{language === "fr" ? "COMBO POPULAIRE" : "POPULAR COMBO"}</span>
                    </div>
                    <span className="text-sm font-bold text-amber-600">$1,099</span>
                  </Link>
                  <Link
                    to="/skin-boosters?highlight=24"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">#24</span>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>The Complete Rejuvenation</span>
                      <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded bg-rose-100 text-rose-600">{language === "fr" ? "COMBO VEDETTE" : "BEST SELLER COMBO"}</span>
                    </div>
                    <span className="text-sm font-bold text-amber-600">$1,599</span>
                  </Link>
                </div>
              </div>
              
              {/* Medical Microneedling Link */}
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                <Link
                  to="/microneedling-ottawa"
                  onClick={() => setOpenDropdown(null)}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-rose-50 to-orange-50 hover:from-rose-100 hover:to-orange-100 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 flex items-center justify-center">
                    <Scissors className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-800 group-hover:text-rose-700" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {language === "fr" ? "Microneedling Médical" : "Medical Microneedling"}
                    </span>
                    <span className="block text-[10px] text-gray-500">
                      {language === "fr" ? "Rajeunissement cutané avancé" : "Advanced skin rejuvenation"}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </Link>
              </div>
            </div>
            
            {/* View All Link - Fixed at bottom */}
            <div className="flex-shrink-0 bg-gray-50 px-4 py-3 border-t border-gray-100">
              <Link 
                to="/skin-boosters" 
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-center gap-2 w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                <span>{language === "fr" ? "Voir les 30 Traitements" : "View All 30 Treatments"}</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-background rounded-lg shadow-2xl border border-border p-5 animate-fade-in z-[9999]">
            <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider border-b border-gray-200 pb-2">
              {t("dropdown.getInTouch")}
            </h3>
            
            {/* Message Us Options */}
            <ul className="space-y-2 mb-4">
              {chatItems.filter(item => item.url).map((item, idx) => {
                const isMessenger = item.url && item.url.includes('m.me/PreciseRNInjectionist');
                return (
                <li key={idx}>
                  <a
                    href={item.url}
                    target={item.url.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    onClick={isMessenger ? (e) => { e.preventDefault(); openContactModal({ source: "navbar-chat-dropdown" }); } : undefined}
                    className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-3 rounded-lg transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color} text-white`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</span>
                      <span className="block text-xs text-gray-500">{item.description}</span>
                    </div>
                  </a>
                </li>
                );
              })}
            </ul>
            
            {/* Contact Form Button */}
            <div className="pt-3 border-t border-gray-100">
              <button 
                onClick={() => { scrollToSection("contact"); setOpenDropdown(null); }} 
                className="flex items-center gap-3 w-full text-left hover:bg-amber-50 p-3 rounded-lg transition-colors"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-500 text-white">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {language === "fr" ? "Formulaire de Contact" : "Contact Form"}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {language === "fr" ? "Envoyez-nous un message" : "Send us a message"}
                  </span>
                </div>
              </button>
            </div>
            
            {/* Response time note */}
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 px-3">
              <Clock className="w-3 h-3" />
              <span>{t("dropdown.usuallyWithin")}</span>
            </div>
          </div>
        );

      case 'finance':
        return (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[420px] bg-background rounded-lg shadow-2xl border border-border p-5 animate-fade-in z-[9999]">
            <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider mb-4 pb-3 border-b border-gray-200">
              {t("dropdown.flexiblePayment")}
            </h3>
            
            {/* Afterpay Section */}
            <div className="mb-5 p-4 bg-gradient-to-br from-teal-50 to-white rounded-lg border border-teal-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-teal-800">{t("dropdown.payIn4")}</span>
                <img 
                  src="https://static.afterpay.com/integration/product-page/logo-afterpay-colour.png" 
                  alt="Afterpay" 
                  className="h-5"
                />
              </div>
              <div className="rounded-lg overflow-hidden mb-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/dcpawf55_Easy%20and%20fast.png"
                  alt="Afterpay - Easy and Fast"
                  className="w-full h-auto"
                />
              </div>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                  {t("dropdown.shopNowPayLater")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                  {t("dropdown.noExtraFees")}
                </li>
              </ul>
            </div>
            
            {/* iFinance Section */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-blue-800">{t("dropdown.monthlyPayments")}</span>
                <span className="text-blue-600 font-bold text-sm">iFinance</span>
              </div>
              <ul className="space-y-1 text-xs text-gray-600 mb-3">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  {t("dropdown.flexibleMonthly")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  {t("dropdown.quickApproval")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  {t("dropdown.financeLarger")}
                </li>
              </ul>
              <a 
                href="https://apply.medicard.com/22470"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium bg-blue-100 hover:bg-blue-200 px-3 py-2 rounded-lg transition-colors"
              >
                <span>{t("dropdown.calculatePayments")}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            
            <div className="mt-4 pt-3 border-t border-gray-200 text-center">
              <button onClick={() => { scrollToSection("payment"); setOpenDropdown(null); }} className="text-sm text-gray-600 hover:text-gray-800 font-medium">
                {t("dropdown.viewAllPaymentOptions")} →
              </button>
            </div>
          </div>
        );

      case 'botoxTreatments':
        return (
          <div 
            className="absolute top-full left-0 mt-2 w-[420px] bg-background rounded-lg shadow-2xl border border-border animate-fade-in z-[9999] flex flex-col"
            style={{ maxHeight: 'min(580px, calc(100vh - 180px))' }}
            onMouseEnter={() => setOpenDropdown('botoxTreatments')}
          >
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
              <div className="flex items-center gap-2 pb-3 border-b border-purple-200">
                <Zap className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">
                  {language === "fr" ? "Traitements Botox" : "Botox Treatments"}
                </h3>
              </div>
              {/* Flat Rate Pricing Notice */}
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <p className="text-[11px] text-emerald-700 leading-tight">
                    <span className="font-bold">{language === "fr" ? "Prix Forfaitaire" : "Flat Rate Pricing"}</span> — {language === "fr" ? "Unités illimitées par zone. Retouches gratuites au suivi de 2 semaines." : "Unlimited units per area. Free touch-ups at 2-week follow-up."}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5">
              <ul className="space-y-2 pb-4">
                {botoxTreatments.map((item, idx) => (
                  <li key={idx} className="relative">
                    {item.badge && (
                      <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow z-10">
                        {item.badge}
                      </span>
                    )}
                    <button
                      onClick={() => { scrollToSection("services"); setOpenDropdown(null); }}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        item.highlight 
                          ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 hover:from-purple-100 hover:to-pink-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                        </div>
                        <p className="font-semibold text-purple-600">{item.price}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 flex justify-between items-center">
              <button onClick={() => { scrollToSection("services"); setOpenDropdown(null); }} className="text-sm text-gray-600 hover:text-gray-800">
                {language === "fr" ? "Voir les services" : "View services"} →
              </button>
              <Link to="/botox-ottawa" onClick={() => setOpenDropdown(null)} className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                {language === "fr" ? "Guide Botox" : "Botox Guide"} →
              </Link>
            </div>
          </div>
        );

      case 'fillerTreatments':
        return (
          <div 
            className="absolute top-full left-0 mt-2 w-[420px] bg-background rounded-lg shadow-2xl border border-border animate-fade-in z-[9999] flex flex-col"
            style={{ maxHeight: 'min(520px, calc(100vh - 180px))' }}
            onMouseEnter={() => setOpenDropdown('fillerTreatments')}
          >
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
              <div className="flex items-center gap-2 pb-3 border-b border-rose-200">
                <Droplets className="w-5 h-5 text-rose-500" />
                <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">
                  {language === "fr" ? "Remplissage Dermique" : "Dermal Fillers"}
                </h3>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5">
              <ul className="space-y-2 pb-4">
                {fillerTreatments.map((item, idx) => (
                  <li key={idx} className="relative">
                    {item.badge && (
                      <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow z-10">
                        {item.badge}
                      </span>
                    )}
                    <button
                      onClick={() => { scrollToSection("services"); setOpenDropdown(null); }}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        item.highlight 
                          ? 'bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 hover:from-rose-100 hover:to-pink-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{item.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                        </div>
                        <p className="font-semibold text-rose-600">{item.price}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 flex justify-between items-center">
              <button onClick={() => { scrollToSection("services"); setOpenDropdown(null); }} className="text-sm text-gray-600 hover:text-gray-800">
                {language === "fr" ? "Voir les services" : "View services"} →
              </button>
              <Link to="/lip-filler-ottawa" onClick={() => setOpenDropdown(null)} className="text-sm text-rose-600 hover:text-rose-700 font-medium">
                {language === "fr" ? "Guide Remplissage" : "Filler Guide"} →
              </Link>
            </div>
          </div>
        );


      case 'laserSkin':
        return (
          <div
            className="absolute top-full left-0 mt-2 w-[380px] max-h-[75vh] overflow-y-auto bg-background rounded-lg shadow-2xl border border-border p-4 animate-fade-in z-[9999]"
            onMouseEnter={() => setOpenDropdown('laserSkin')}
            data-testid="navbar-laser-dropdown"
          >
            <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: '#a984552a' }}>
              <Sparkles className="w-4 h-4" style={{ color: '#a98455' }} />
              <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider">
                {language === "fr" ? "Laser et HydraFacial" : "Laser & HydraFacial"}
              </h3>
              <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-full" style={{ backgroundColor: '#a98455', color: 'white' }}>New</span>
            </div>

            <div className="mb-3 p-2.5 rounded-lg" style={{ backgroundColor: '#faf8f4' }}>
              <p className="text-xs text-gray-700">
                {language === "fr"
                  ? "Soins laser et HydraFacial de qualité médicale. Peau lisse et radieuse, confiance durable."
                  : "Medical-grade laser hair removal and Hydrafacial. Smooth, radiant skin and long-lasting confidence."}
              </p>
            </div>

            <div className="space-y-1.5 mb-3">
              <Link
                to="/laser-hair-removal"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-house-package"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Promo Maison · Achetez 5 → la 6ᵉ GRATUITE" : "The House Package · Buy 5 → 6th FREE"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr" ? "Combinable toutes zones" : "Stack any mix of areas"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/laser-hair-removal"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-by-area"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Laser · par zone" : "Laser · By Area"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr" ? "Dès 15 $ · visage, jambes, brésilien…" : "From $15 · face, legs, Brazilian…"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/hydrafacial"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg border-2 transition-colors relative"
                style={{ background: 'linear-gradient(135deg, #faf8f4, white)', borderColor: '#a98455' }}
                data-testid="navbar-laser-hydrafacial"
              >
                <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 text-[8px] font-bold rounded-full" style={{ backgroundColor: '#a98455', color: 'white' }}>
                  POPULAR
                </span>
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "HydraFacial Crown" : "Crown HydraFacial"}
                  </span>
                  <span className="block text-[10px]" style={{ color: '#a98455' }}>
                    {language === "fr" ? "Signature, Deluxe, Platinum" : "Signature, Deluxe, Platinum"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/laser-and-skin#rf-microneedling"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg border-2 transition-colors relative"
                style={{ background: 'linear-gradient(135deg, #faf8f4, white)', borderColor: '#a98455' }}
                data-testid="navbar-laser-rf-microneedling"
              >
                <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 text-[8px] font-bold rounded-full" style={{ backgroundColor: '#a98455', color: 'white' }}>
                  NEW
                </span>
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "RF Microneedling" : "RF Microneedling"}
                  </span>
                  <span className="block text-[10px]" style={{ color: '#a98455' }}>
                    {language === "fr" ? "Raffermissement & cicatrices · Dès 250 $ · Ottawa avec une esthéticienne médicale" : "Skin tightening & scars · From $250 · Ottawa with a Medical Aesthetician"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/laser-hair-removal"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-hair-removal"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Épilation Laser" : "Laser Hair Removal"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr"
                      ? "Cynosure Elite+ · Visage · Corps · Brésilien"
                      : "Cynosure Elite+ · Face · Body · Brazilian"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              {/* Add-Ons quick-jump — added per Karine's request so clients
                  can browse the upsell menu directly from the main nav
                  instead of scrolling to the bottom of the Laser & Skin
                  page. Anchors to the same in-page section. */}
              <Link
                to="/laser-and-skin#addons"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-addons"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Compléments de Traitement" : "Treatment Add-Ons"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr"
                      ? "Diamond Tip · CO₂ · LED · Lymphatique · Microneedling"
                      : "Diamond Tip · CO₂ · LED · Lymphatic · Microneedling"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              {/* Sept 3 2026 · Karine ask — Lash & Brow Studio wasn't
                  discoverable from anywhere in the nav. Route lives at
                  /lash-brow-studio; SEO aliases /brow-lamination-ottawa
                  and /lash-lift-ottawa. Ottawa aesthetician only. */}
              <Link
                to="/lash-brow-studio"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-lash-brow-studio"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Studio Cils & Sourcils" : "Lash & Brow Studio"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr"
                      ? "Rehaussement cils 65 $ · Laminage sourcils 59 $ · Regard complet 119 $"
                      : "Lash Lift & Tint $65 · Brow Lamination $59 · Eye Makeover $119"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/laser-hair-removal"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-head-to-toe"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Tête-aux-pieds Illimité" : "Head-to-Toe Unlimited"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr" ? "249 $/mo × 6 · tout-inclus" : "$249/mo × 6 · all-inclusive"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>

              <Link
                to="/laser-hair-removal"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors"
                style={{ borderColor: '#a9845522' }}
                data-testid="navbar-laser-full-menu"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === "fr" ? "Voir le menu complet" : "See the full menu"}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {language === "fr" ? "22 zones · dès 15 $" : "22 areas · from $15"}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#a98455' }} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-3 text-[10px] text-gray-600">
              <div className="flex items-center gap-1"><CreditCard className="w-3 h-3" style={{ color: '#a98455' }} /><span>Afterpay 4×</span></div>
              <div className="flex items-center gap-1"><Crown className="w-3 h-3" style={{ color: '#a98455' }} /><span>Earn Crown Rewards</span></div>
              <div className="flex items-center gap-1"><Heart className="w-3 h-3" style={{ color: '#a98455' }} /><span>Safe for most skin</span></div>
              <div className="flex items-center gap-1"><Award className="w-3 h-3" style={{ color: '#a98455' }} /><span>Medical-grade</span></div>
            </div>

            <Link
              to="/laser-and-skin"
              onClick={() => setOpenDropdown(null)}
              className="block w-full text-center px-3 py-2 rounded-full text-xs font-semibold transition-all"
              style={{ background: 'linear-gradient(135deg, #a98455, #c9a86b)', color: 'white' }}
              data-testid="navbar-laser-view-all"
            >
              {language === "fr" ? "Voir tous les traitements" : "View all treatments"} →
            </Link>
          </div>
        );

      case 'sculptra':
        return (
          <div 
            className="absolute top-full left-0 mt-2 w-[380px] max-h-[70vh] overflow-y-auto bg-background rounded-lg shadow-2xl border border-border p-4 animate-fade-in z-[9999]"
            onMouseEnter={() => setOpenDropdown('sculptra')}
          >
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200">
              <Heart className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider">
                {language === "fr" ? "Bio-Stimulateur de Collagène" : "Collagen Bio-Stimulator"}
              </h3>
            </div>
            
            {/* What is Sculptra */}
            <div className="mb-3 p-2 bg-amber-50 rounded-lg">
              <p className="text-xs text-gray-700">
                {language === "fr" 
                  ? "Sculptra stimule VOTRE corps à produire son propre collagène. Résultats naturels durant jusqu'à 2 ans."
                  : "Sculptra stimulates YOUR body to produce its own collagen. Natural results lasting up to 2 years."}
              </p>
            </div>
            
            {/* Pricing Options - Compact */}
            <div className="space-y-1.5 mb-3">
              <Link
                to="/sculptra"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-colors"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">{language === "fr" ? "1 Flacon" : "1 Vial"}</span>
                  <span className="block text-[10px] text-gray-500">{language === "fr" ? "Zone ciblée" : "Targeted area"}</span>
                </div>
                <span className="text-base font-bold text-amber-600">$799</span>
              </Link>
              
              <Link
                to="/sculptra"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 hover:from-amber-100 hover:to-yellow-100 transition-colors relative"
              >
                <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 bg-amber-500 text-white text-[8px] font-bold rounded-full">
                  {language === "fr" ? "POPULAIRE" : "POPULAR"}
                </span>
                <div>
                  <span className="text-sm font-medium text-gray-900">{language === "fr" ? "2 Flacons" : "2 Vials"}</span>
                  <span className="block text-[10px] text-amber-700">{language === "fr" ? "Résultats visibles" : "Visible results"}</span>
                </div>
                <span className="text-base font-bold text-amber-600">$1,500</span>
              </Link>
              
              <Link
                to="/sculptra"
                onClick={() => setOpenDropdown(null)}
                className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-colors"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">{language === "fr" ? "4 Flacons" : "4 Vials"}</span>
                  <span className="block text-[10px] text-gray-500">{language === "fr" ? "Meilleure valeur" : "Best value"}</span>
                </div>
                <span className="text-base font-bold text-amber-600">$2,699</span>
              </Link>
            </div>
            
            {/* Key Benefits - 2x2 compact */}
            <div className="grid grid-cols-2 gap-1.5 mb-3 text-[10px] text-gray-600">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-amber-500" />
                <span>{language === "fr" ? "2+ ans" : "2+ years"}</span>
              </div>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span>{language === "fr" ? "Naturel" : "Natural"}</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-amber-500" />
                <span>FDA</span>
              </div>
              <div className="flex items-center gap-1">
                <Award className="w-3 h-3 text-amber-500" />
                <span>95%</span>
              </div>
            </div>
            
            <Link 
              to="/sculptra" 
              onClick={() => setOpenDropdown(null)}
              className="flex items-center justify-center gap-2 w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <span>{language === "fr" ? "En Savoir Plus" : "Learn More"}</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
    <nav
      data-testid="navbar"
      className="fixed left-0 right-0 z-[100]"
      style={{
        top: "calc(var(--season-banner-h, 0px) + var(--kl-navbar-offset, 41px))",
        background: "#FAF5EB",  /* Soft Ivory White per spec */
        boxShadow: "inset 0 -1px 0 #B8862B, 0 8px 24px rgba(0,0,0,0.06)",  /* 1px gold hairline + luxury drop */
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 xl:px-10">
        {/* Logo Section */}
        <div className="flex items-center justify-between py-1 md:py-1.5 md:h-[104px] md:overflow-visible gap-3 lg:gap-6 xl:gap-8" data-testid="header-plaque">
          {/* Mobile: Hamburger Menu on left | Desktop: Search */}
          <div className="w-10 md:w-auto flex-shrink-0">
            {/* Mobile Hamburger Menu */}
            <button
              className="md:hidden text-gray-700 p-1 flex flex-col items-center"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              <span className="text-[9px] font-medium leading-tight mt-0.5">Menu</span>
            </button>
            
            {/* Desktop Search */}
            <button 
              onClick={() => setIsSearchOpen(true)}
              className="hidden md:flex items-center gap-2 text-[#1F1611] hover:text-[#B8862B] transition-colors"
            >
              <Search className="w-5 h-5" />
              <span className="text-sm">{t("nav.search")}</span>
            </button>
          </div>
          
          {/* Crown Reserve · Rewards Badge — primary CTA.
              Bold espresso-outlined polished gold with brighter core &
              softer glow ring so it commands the nav bar and reads as
              a jewelry medal, not a paint chip. */}
          <Link 
            to="/crown-reserve"
            className="hidden lg:flex items-center gap-3 px-4 py-2.5 rounded-full transition-all group relative overflow-hidden flex-shrink-0 hover:scale-[1.03]"
            style={{
              // Ocean-jewel palette: deep navy → teal → aqua → seafoam → emerald
              // Bright, cool, unmistakable in a warm cream navbar. Feels like a
              // polished piece of turquoise inlaid in silver.
              background: "linear-gradient(135deg, #062e4f 0%, #0b6b7a 22%, #14b8a6 45%, #5eead4 55%, #14b8a6 65%, #0b6b7a 80%, #043a2e 100%)",
              backgroundSize: "220% 220%",
              border: "2px solid #062033",
              boxShadow: "0 0 0 1.5px #5eead4, 0 10px 28px rgba(20,184,166,0.55), 0 4px 12px rgba(6,32,51,0.35), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.18)",
              animation: "crownGradient 5s ease infinite",
            }}
            data-testid="crown-reserve-badge"
          >
            {/* Traveling shine — sweeps across on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            
            {/* Crown icon */}
            <div className="relative flex-shrink-0">
              <Crown className="w-5 h-5 text-white drop-shadow-[0_1px_2px_rgba(6,32,51,0.55)]" strokeWidth={2.5} />
              <Sparkles className="w-3 h-3 text-white absolute -top-1 -right-1 animate-pulse drop-shadow-[0_0_4px_rgba(255,255,255,0.9)]" />
            </div>
            
            {/* Text — "Members" eyebrow + "Rewards Points" + "Crown Reserve" subtitle */}
            <div className="text-left relative leading-tight">
              <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-white/95 whitespace-nowrap drop-shadow-[0_1px_1px_rgba(6,32,51,0.55)]">
                {language === "fr" ? "Membres" : "Members"}
              </p>
              <p
                className="text-[15px] font-bold text-white tracking-wide whitespace-nowrap drop-shadow-[0_1px_2px_rgba(6,32,51,0.7)]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {language === "fr" ? "Points Récompenses" : "Rewards Points"}
              </p>
              <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/95 whitespace-nowrap drop-shadow-[0_1px_1px_rgba(6,32,51,0.55)]">
                {language === "fr" ? "Crown Reserve · Inscrivez-vous" : "Crown Reserve · Sign Up & Save"}
              </p>
            </div>

            {/* $60 welcome-bonus coin — deep-sea navy with aqua text */}
            <span
              className="relative inline-flex items-center justify-center min-w-[38px] h-[34px] px-2 rounded-full bg-[#062033] text-[#5eead4] text-[11px] font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.18),_0_4px_12px_rgba(0,0,0,0.45)] border border-[#5eead4] flex-shrink-0"
              aria-hidden
            >
              +$60
            </span>
          </Link>
          
          {/* Center Logo — the KLC plaque (navy square + gold interlocking
              KL monogram + wordmark + Crown Reserve rule + 3-city tagline).
              Stand-alone, no headshot — the plaque carries the whole brand. */}
          <div
            className="flex items-center gap-2 md:gap-3 cursor-pointer min-w-0"
            onClick={() => scrollToSection("hero")}
            data-testid="nav-logo"
          >
            <img 
              src="/logos/klc_quiet_lux_transparent.png" 
              alt="Karine Lauzon RN Medical Aesthetics Clinics — Ottawa · Kanata · Carleton Place" 
              className="w-auto h-[44px] sm:h-[54px] md:h-[68px] lg:h-[76px] max-w-[92vw] md:max-w-[74vw] object-contain flex-shrink-0"
              data-testid="brand-plaque"
              onError={(e) => {
                // Fallback chain: emerald plaque → tight legacy → full plaque → backlit → original
                if (e.target.src.indexOf('klc_official_logo_tight') === -1 && e.target.src.indexOf('klc_official_logo.png') === -1 && e.target.src.indexOf('kl-logo-backlit') === -1 && e.target.src.indexOf('karine-lauzon-logo') === -1) {
                  e.target.src = '/logos/klc_official_logo_tight.png';
                } else if (e.target.src.indexOf('kl-logo-backlit') === -1 && e.target.src.indexOf('karine-lauzon-logo') === -1) {
                  e.target.src = '/signage/kl-logo-backlit.png';
                } else if (e.target.src.indexOf('karine-lauzon-logo') === -1) {
                  e.target.src = '/karine-lauzon-logo.png';
                }
              }}
            />
            {/* Audio button - hidden on mobile */}
            <div className="hidden sm:block flex-shrink-0">
              <KarineAudioButton size="xs" showLabel={false} />
            </div>
          </div>

          {/* Mobile: Search & Social | Desktop: Text & Social */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0 justify-end">
            {/* Desktop right-cluster — unified into a single tidy 2-row
                block Feb 24 2026 (owner ask). Row 1 = tiny chips
                (Sign In / Log out / FB / Insta / FR). Row 2 = the flagship
                My Account pill w/ inlined points sub-chip. Total footprint
                stays compact so the KL plaque has breathing room even on
                mid-width desktop and iPad. */}
            <div className="hidden md:flex flex-col items-end gap-1 mr-1 lg:mr-2">
              {/* Row 1 — chips: sign-in link + tiny socials + lang toggle */}
              <div className="flex items-center gap-1.5">
                <a
                  href="/my-appointments"
                  className="text-[11px] font-semibold tracking-wide hover:underline underline-offset-2"
                  style={{ color: "#1F1611", fontFamily: "'Playfair Display', serif" }}
                  data-testid="client-portal-signin"
                >
                  {language === "fr" ? "Se connecter" : "Sign In"}
                </a>
                <ClientLogoutLink language={language} />
                <a
                  href="https://www.facebook.com/PreciseRNInjectionist"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center justify-center w-6 h-6 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-full transition-colors"
                  data-testid="header-facebook"
                  aria-label="Facebook"
                >
                  <Facebook className="w-3 h-3" />
                </a>
                <a
                  href="https://www.instagram.com/karine_precisioninjectionist"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center justify-center w-6 h-6 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white rounded-full transition-opacity"
                  data-testid="header-instagram"
                  aria-label="Instagram"
                >
                  <Instagram className="w-3 h-3" />
                </a>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleLanguage(); }}
                  className="flex items-center gap-1 px-2 h-6 rounded-full bg-transparent hover:bg-[#B8862B]/10 border border-[#B8862B]/70 text-[#1F1611] text-[10px] font-bold uppercase tracking-wider transition-colors"
                  data-testid="header-lang-toggle"
                  aria-label="Toggle language"
                >
                  <Globe className="w-3 h-3" />
                  <span>{language === "en" ? "FR" : "EN"}</span>
                </button>
              </div>
              {/* Row 2 — the flagship My Account pill */}
              <div className="flex items-center gap-2">
                  <div
                    className="relative"
                    onMouseEnter={() => setPillHover(true)}
                    onMouseLeave={() => setPillHover(false)}
                  >
                  <a
                    ref={pillRef}
                    href={clientPoints !== null ? "/my-appointments?focus=points" : "/my-appointments"}
                    className={`flex items-center gap-2 group px-3 py-1.5 rounded-full shadow hover:shadow-lg transition-all ${milestonePulse ? "kl-tier-milestone-pulse" : ""}`}
                    style={{ background: "#3D2E20", border: "1px solid rgba(200,162,75,0.55)" }}
                    data-testid="client-portal-cta"
                    data-milestone-pulse={milestonePulse ? "true" : "false"}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#5A3F27"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#3D2E20"; }}
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #C7982E, #B8862B)" }}>
                      <Crown className="w-3 h-3" style={{ color: "#1F1611" }} />
                    </div>
                    <span className="text-xs font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: "#FFFFFF" }}>
                      {language === "fr" ? "Mon Compte" : "My Account"}
                    </span>
                    {/* Points sub-chip — Feb 24 2026: inlined into the
                        My Account pill so we don't stack two overlapping
                        pills over the plaque. Only rendered once the
                        signed-in client's points API resolves. */}
                    {clientPoints !== null && (
                      <span
                        className="ml-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tabular-nums"
                        style={{
                          background: "rgba(199,152,46,0.16)",
                          border: "1px solid rgba(212,165,116,0.45)",
                          color: "#F4D577",
                          fontFamily: "'Playfair Display', serif",
                        }}
                        data-testid="client-portal-cta-points"
                        title={`${clientPoints.toLocaleString()} Crown Points${clientCredit > 0 ? ` · $${clientCredit} credit` : ""}`}
                      >
                        {clientPoints.toLocaleString()}
                        {clientCredit > 0 ? ` · $${clientCredit}` : ""}
                      </span>
                    )}
                    <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" style={{ color: "#C7982E" }} />
                  </a>

                  {/* Tier-progress bubble (Feb 24 2026): hover the pill
                      → surface the "next tier" motivation copy so
                      dormant clients see how close they are to their
                      next redemption. Only when signed in AND another
                      tier is still ahead of them. */}
                  {pillHover && clientPoints !== null && clientNextTier && (
                    <div
                      className="absolute right-0 top-full mt-2 w-64 rounded-xl p-3 shadow-2xl z-50"
                      style={{
                        background: "linear-gradient(135deg, #1F1611 0%, #3D2E20 100%)",
                        border: "1px solid rgba(212,165,116,0.5)",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
                      }}
                      data-testid="client-portal-cta-tier-bubble"
                    >
                      {/* Little arrow up-right */}
                      <span
                        aria-hidden
                        className="absolute -top-1.5 right-6 w-3 h-3 rotate-45"
                        style={{ background: "#1F1611", borderLeft: "1px solid rgba(212,165,116,0.5)", borderTop: "1px solid rgba(212,165,116,0.5)" }}
                      />
                      <p
                        className="text-[9px] uppercase tracking-widest font-bold"
                        style={{ color: "rgba(244,213,119,0.75)" }}
                      >
                        {language === "fr" ? "Prochain palier" : "Next tier"}
                      </p>
                      <p
                        className="text-sm font-bold mt-0.5"
                        style={{ color: "#F4D577", fontFamily: "'Playfair Display', serif" }}
                        data-testid="client-portal-cta-tier-label"
                      >
                        {language === "fr"
                          ? `${clientPointsToNext.toLocaleString()} pts pour ${clientNextTier.label}`
                          : `${clientPointsToNext.toLocaleString()} pts to ${clientNextTier.label}`}
                      </p>
                      {/* Progress bar — how close they are to the next tier */}
                      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(212,165,116,0.15)" }}>
                        <div
                          className="h-full transition-[width] duration-500"
                          style={{
                            width: `${Math.min(100, Math.max(6, Math.round((clientPoints / clientNextTier.points) * 100)))}%`,
                            background: "linear-gradient(90deg, #C7982E, #F4D577)",
                          }}
                        />
                      </div>
                      <p
                        className="text-[10px] mt-2"
                        style={{ color: "rgba(255,255,255,0.65)" }}
                      >
                        {language === "fr"
                          ? `Vous avez ${clientPoints.toLocaleString()} pts · Débloque ${clientNextTier.label} à ${clientNextTier.points.toLocaleString()} pts.`
                          : `You have ${clientPoints.toLocaleString()} pts · Unlock ${clientNextTier.label} at ${clientNextTier.points.toLocaleString()} pts.`}
                      </p>
                      {/* Sound preference — Feb 24 2026 Karine ask.
                          Client opts in to a soft "cha-ching" chord that
                          plays alongside the pulse the first time they
                          cross a tier. Muted by default. Toggle is inside
                          the hover bubble so it stays out of the way
                          until a client explicitly hunts for it. Made
                          `pointer-events-auto` so the toggle can be
                          clicked (the bubble itself is pointer-none). */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const next = !milestoneSound;
                          setMilestoneSoundState(next);
                          setMilestoneSoundEnabled(next);
                          // Let the client hear a preview when turning ON.
                          if (next) playMilestoneChord({ forcePlay: true });
                        }}
                        className="mt-3 w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[10px] font-semibold"
                        style={{
                          background: milestoneSound ? "rgba(199,152,46,0.22)" : "rgba(255,255,255,0.06)",
                          border: `1px solid ${milestoneSound ? "rgba(212,165,116,0.55)" : "rgba(255,255,255,0.15)"}`,
                          color: milestoneSound ? "#F4D577" : "rgba(255,255,255,0.7)",
                        }}
                        data-testid="milestone-sound-toggle"
                        data-milestone-sound-on={milestoneSound ? "true" : "false"}
                      >
                        <span className="uppercase tracking-widest">
                          {milestoneSound
                            ? (language === "fr" ? "Son activé" : "Sound on")
                            : (language === "fr" ? "Son du palier" : "Milestone sound")}
                        </span>
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full"
                          style={{ background: milestoneSound ? "#C7982E" : "transparent", border: milestoneSound ? "none" : "1px solid rgba(255,255,255,0.35)" }}
                        >
                          {/* Simple bell glyph — no extra icon import needed */}
                          <span aria-hidden style={{ color: milestoneSound ? "#1F1611" : "rgba(255,255,255,0.7)", fontSize: "9px", lineHeight: 1 }}>♪</span>
                        </span>
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              </div>

            {/* Mobile actions — FB/IG were crowding the plaque wordmark.
                Removed here; both remain accessible in the hamburger menu
                and footer. Keep only the language toggle so the plaque
                has full breathing room on small screens. */}
            <div className="flex md:hidden items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); toggleLanguage(); }}
                className="ml-1 flex items-center gap-1 px-2 h-7 rounded-full bg-[#1F1611] hover:bg-[#3D2E20] text-[#F4D577] text-[10px] font-bold uppercase tracking-wider transition-colors border border-[#B8862B]/60"
                data-testid="header-lang-toggle-mobile"
                aria-label="Toggle language"
              >
                <Globe className="w-3 h-3" />
                <span>{language === "en" ? "FR" : "EN"}</span>
              </button>
            </div>
            
            {/* Mobile Search Icon */}
            <button 
              onClick={() => setIsSearchOpen(true)}
              className="md:hidden text-gray-800 hover:text-black transition-colors p-1"
            >
              <Search className="w-5 h-5" />
            </button>
            
            {/* Text Us - Desktop only */}
            <a
              href="sms:613-850-0805?body=Hi, I'd like to book an appointment"
              className="hidden text-gray-800 hover:text-black transition-colors text-sm"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{t("topbar.textUs")} (613) 850-0805</span>
            </a>

            {/* Social + language toggle moved INTO the unified 2-row
                right-block above (Feb 24 2026 Karine ask). Keeping this
                comment as a breadcrumb so nobody re-adds a duplicate row. */}
          </div>
        </div>

      </div>

      {/* Navigation Links - Desktop Only - FULL WIDTH */}
      <div 
        ref={navBannerRef}
        className="hidden md:block border-t border-b border-[#B8862B]/40 overflow-x-auto scrollbar-hide nav-fade-right relative"
        style={{ background: "linear-gradient(180deg, #F5EEE1 0%, #F0E9DC 100%)", boxShadow: "0 4px 12px rgba(15, 47, 90, 0.08), inset 0 1px 0 rgba(255,255,255,0.55)" }}
        data-testid="nav-banner-menu"
        onMouseLeave={(e) => {
          // Close any open dropdown when the cursor leaves the nav region,
          // but not if it's heading into the floating mega-menu wrapper.
          const rt = e.relatedTarget;
          if (rt && typeof rt.closest === 'function' && rt.closest('[data-testid$="-wrapper"]')) {
            return;
          }
          scheduleClose(350);
        }}
      >
        {/* iPad-tightened (Feb 24 2026): smaller gaps + tighter font at
            md-lg so all 9 items + Virtual chip fit on iPad landscape
            without the last tab getting clipped. Full luxe spacing
            returns at xl (≥ 1280 px). */}
        <div className="max-w-7xl mx-auto flex items-center justify-start xl:justify-center py-2.5 gap-1.5 md:gap-2 lg:gap-3 xl:gap-5 px-3 lg:px-5 xl:px-6 min-w-max" ref={dropdownRef}>
          {navLinks.map((link) => (
            link.hasDropdown ? (
              <div 
                key={link.id} 
                className="relative group"
                onMouseEnter={() => openOnHover(link.dropdownType)}
              >
                {/* Main clickable label - navigates to page if href exists */}
                <div className="flex items-center">
                  {link.href ? (
                    <Link
                      to={link.href}
                      onClick={() => setOpenDropdown(null)}
                      className={`text-[11px] md:text-[11px] lg:text-[12.5px] xl:text-sm tracking-tight transition-all duration-200 py-1 relative whitespace-nowrap ${
                        link.isFeaturedPromotions
                          // Promotions tab — filled crimson gradient pill
                          // (Karine ask, Sep 28 2026 · greater visibility).
                          // Matches the visual weight of the champagne
                          // Treatments pill so the two hero CTAs read as
                          // a paired trio with the navy Full Menu button.
                          // "LIVE" pulsing dot signals active offers.
                          ? `px-3.5 py-1.5 rounded-full border-2 nav-hot-pulse flex items-center gap-1.5 ${
                              openDropdown === link.dropdownType
                                ? 'border-red-800 bg-gradient-to-b from-red-700 to-red-800 text-white shadow-[0_4px_14px_-3px_rgba(220,38,38,0.6)]'
                                : 'border-red-600 bg-gradient-to-b from-red-500 via-red-600 to-red-700 text-white hover:from-red-600 hover:to-red-800 shadow-[0_3px_10px_-3px_rgba(220,38,38,0.5)] hover:shadow-[0_5px_14px_-3px_rgba(220,38,38,0.7)]'
                            } font-extrabold uppercase tracking-[0.08em]`
                          : link.isFeatured
                          // Treatments tab — the single most important link in
                          // the nav (Karine, Jun 30 2026). Boxed in champagne
                          // gold so it visually pops above every other tab.
                          // Once-per-session pulse pulls the eye in on first
                          // page load — gated by sessionStorage so it never
                          // becomes annoying.
                          ? `px-3.5 py-1.5 rounded-full border-2 nav-featured-pulse ${
                              openDropdown === link.dropdownType
                                ? 'border-amber-600 bg-gradient-to-b from-amber-100 to-amber-200 text-amber-950 shadow-[0_4px_14px_-3px_rgba(180,140,60,0.55)]'
                                : 'border-amber-500 bg-gradient-to-b from-amber-50 via-amber-100/80 to-amber-50 text-amber-900 hover:border-amber-600 hover:from-amber-100 hover:to-amber-200 shadow-[0_3px_10px_-3px_rgba(180,140,60,0.45)] hover:shadow-[0_5px_14px_-3px_rgba(180,140,60,0.6)]'
                            } font-extrabold uppercase tracking-[0.08em]`
                          : `border-b-2 ${
                              openDropdown === link.dropdownType
                                ? 'border-amber-700 text-amber-900 font-bold'
                                : link.dropdownType === 'promotions'
                                  ? 'border-transparent text-red-700 hover:text-red-900 hover:border-red-500 font-bold'
                                  : link.dropdownType === 'beautyHub' 
                                    ? 'border-transparent text-amber-800 hover:text-amber-950 hover:border-amber-600 font-semibold' 
                                    : link.highlight
                                      ? 'border-amber-500 text-amber-700 hover:text-amber-900 font-bold'
                                      : 'border-transparent text-stone-700 hover:text-stone-950 hover:border-stone-400 font-medium'
                            }`
                      }`}
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      data-testid={`nav-${link.id}`}
                    >
                      {link.isFeaturedPromotions ? (
                        <>
                          <Tag className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                          <span
                            aria-hidden="true"
                            className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shadow-[0_0_6px_rgba(255,255,255,0.9)]"
                            data-testid="nav-promo-live-dot"
                          />
                        </>
                      ) : (
                        link.dropdownType === 'promotions' && <Tag className="w-3.5 h-3.5 text-red-600 mr-1" />
                      )}
                      {link.isFeatured && (
                        <span className="inline-block mr-1 text-amber-700">✦</span>
                      )}
                      {navLabel(link)}
                      {link.isHiring && (
                        <span
                          className="absolute -top-2.5 -right-2 px-1.5 py-0.5 bg-rose-600 text-white text-[8px] font-black uppercase tracking-widest rounded-full animate-pulse shadow-md leading-none"
                          data-testid={`nav-hiring-badge-${link.id}`}
                        >
                          Hiring
                        </span>
                      )}
                      {link.dropdownType === 'beautyHub' && (() => {
                        const day = new Date().getDay();
                        return day >= 1 && day <= 5;
                      })() && (
                        <span className="absolute -top-2 -right-1 px-1 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-amber-600 text-white rounded-full animate-pulse leading-none shadow-sm">
                          new
                        </span>
                      )}
                      {link.highlight && (
                        <span className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full animate-pulse shadow-md">
                          NEW!
                        </span>
                      )}
                      {/* Open-state caret — tiny chevron under the link that
                          anchors the floating mega-menu to its trigger. */}
                      {openDropdown === link.dropdownType && (
                        <span
                          aria-hidden="true"
                          className="absolute left-1/2 -translate-x-1/2 -bottom-[7px] w-2.5 h-2.5 bg-background border-l border-t border-amber-300 rotate-45 z-[10000] pointer-events-none animate-in fade-in zoom-in duration-200"
                          data-testid={`nav-caret-${link.id}`}
                        />
                      )}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleNavClick(link)}
                      className={`text-[11px] md:text-[11px] lg:text-[12.5px] xl:text-sm tracking-tight transition-all duration-200 py-1 border-b-2 relative whitespace-nowrap ${
                        openDropdown === link.dropdownType
                          ? 'border-amber-700 text-amber-900 font-bold'
                          : link.dropdownType === 'promotions'
                            ? 'border-transparent text-red-700 hover:text-red-900 hover:border-red-500 font-bold'
                            : 'border-transparent text-stone-700 hover:text-stone-950 hover:border-stone-400 font-medium'
                      }`}
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      data-testid={`nav-${link.id}`}
                    >
                      {link.dropdownType === 'promotions' && <Tag className="w-3.5 h-3.5 text-red-600 mr-1" />}
                      {navLabel(link)}
                      {openDropdown === link.dropdownType && (
                        <span
                          aria-hidden="true"
                          className="absolute left-1/2 -translate-x-1/2 -bottom-[7px] w-2.5 h-2.5 bg-background border-l border-t border-amber-300 rotate-45 z-[10000] pointer-events-none animate-in fade-in zoom-in duration-200"
                          data-testid={`nav-caret-${link.id}`}
                        />
                      )}
                    </button>
                  )}
                  {/* Dropdown chevron - always visible for dropdown items */}
                  <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${
                    openDropdown === link.dropdownType ? 'rotate-180' : ''
                  } ${
                    openDropdown === link.dropdownType 
                      ? 'text-amber-900' 
                      : link.dropdownType === 'promotions' 
                        ? 'text-red-600' 
                        : 'text-stone-500'
                  }`} />
                </div>
                {/* Invisible bridge to prevent dropdown from closing when moving mouse */}
                {openDropdown === link.dropdownType && (
                  <div className="absolute top-full left-0 w-full h-3 bg-transparent" />
                )}
                {openDropdown === link.dropdownType && renderDropdown(link.dropdownType)}
              </div>
            ) : (
              <button
                key={link.id}
                onClick={() => handleNavClick(link)}
                className={`text-[11px] md:text-[11px] lg:text-[12.5px] xl:text-sm tracking-tight transition-all duration-200 py-1 relative whitespace-nowrap ${
                  link.isFullMenu
                    // Full Menu — outlined navy pill (Sep 28 2026 · Karine ask).
                    // Pairs with the crimson Promotions pill and the champagne
                    // Treatments pill so the three primary nav CTAs read as a
                    // deliberate trio. Navy = "the complete authoritative
                    // catalog" — chosen to contrast (not compete) with the
                    // warm champagne + crimson.
                    ? 'px-3.5 py-1.5 rounded-full border-2 border-slate-800 bg-white/70 hover:bg-slate-900 text-slate-900 hover:text-white font-extrabold uppercase tracking-[0.08em] shadow-[0_3px_10px_-3px_rgba(15,23,42,0.35)] hover:shadow-[0_5px_14px_-3px_rgba(15,23,42,0.55)] flex items-center gap-1.5 border-b-2'
                    : link.isFreeConsult
                    // Book FREE Consultation — filled emerald pill with a soft
                    // pulse (Oct 22 2026 · Karine ask: "make it stand out").
                    ? 'px-3.5 py-1.5 rounded-full border-2 border-emerald-700 bg-gradient-to-b from-emerald-500 via-emerald-600 to-emerald-700 text-white hover:from-emerald-600 hover:to-emerald-800 font-extrabold uppercase tracking-[0.08em] shadow-[0_3px_12px_-3px_rgba(5,150,105,0.65)] hover:shadow-[0_5px_16px_-3px_rgba(5,150,105,0.8)] flex items-center gap-1.5 border-b-2 nav-hot-pulse'
                    : `border-b-2 ${
                        link.highlight
                          ? 'text-amber-700 hover:text-amber-900 font-bold border-amber-500 hover:border-amber-700 bg-amber-50 px-3 rounded-full'
                          : link.isBrandGradient
                            // "Buy Skincare" — bridges the two boutique brands with an
                            // Eminence-green → SkinCeuticals-blue gradient so shoppers'
                            // eyes lock onto the retail entrance. Text is filled by the
                            // gradient itself via `bg-clip-text`. Iter 378 · Feb 20 2026.
                            ? 'font-extrabold border-transparent hover:border-emerald-500 nav-shop-shine tracking-wide'
                            : link.isAfterpay
                              ? 'text-stone-700 hover:text-stone-950 font-medium border-transparent hover:border-stone-400 flex items-center'
                              : 'text-stone-700 hover:text-stone-950 font-medium border-transparent hover:border-stone-400'
                      }`
                }`}
                style={{ fontFamily: link.isAfterpay ? 'inherit' : "'Playfair Display', serif" }}
                data-testid={`nav-${link.id}`}
              >
                {link.isFullMenu ? (
                  <>
                    <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span>{navLabel(link)}</span>
                  </>
                ) : link.isFreeConsult ? (
                  <>
                    <CalendarCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span>{navLabel(link)}</span>
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shadow-[0_0_6px_rgba(255,255,255,0.9)]" />
                  </>
                ) : link.isAfterpay ? (
                  <span className="bg-black text-[#b2fce4] text-xs font-bold px-2 py-1 rounded tracking-wide">
                    Afterpay Finance
                  </span>
                ) : link.isBrandGradient ? (
                  <span
                    className="inline-block bg-gradient-to-r from-emerald-700 via-teal-600 to-sky-700 bg-clip-text text-transparent drop-shadow-[0_1px_0_rgba(255,255,255,0.4)]"
                    data-testid="nav-shop-gradient-label"
                  >
                    {navLabel(link)}
                  </span>
                ) : link.isSkinTips ? (
                  <span
                    className="inline-flex items-center gap-1"
                    data-testid="nav-skin-tips-label"
                  >
                    <span className="inline-block bg-gradient-to-r from-emerald-700 via-teal-600 to-amber-600 bg-clip-text text-transparent font-extrabold tracking-wide">
                      {navLabel(link)}
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-600 text-white text-[8px] font-black uppercase tracking-widest rounded-full shadow-sm">
                      50
                    </span>
                  </span>
                ) : link.isHiring ? (
                  <>
                    {navLabel(link)}
                    <span
                      className="absolute -top-2.5 -right-2 px-1.5 py-0.5 bg-rose-600 text-white text-[8px] font-black uppercase tracking-widest rounded-full animate-pulse shadow-md leading-none"
                      data-testid={`nav-hiring-badge-${link.id}`}
                    >
                      Hiring
                    </span>
                  </>
                ) : link.highlight ? (
                  <>
                    {navLabel(link)}
                    <span className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full animate-pulse shadow-md">
                      NEW!
                    </span>
                  </>
                ) : (
                  navLabel(link)
                )}
              </button>
            )
          ))}
          
          <div className="w-px h-6 bg-stone-300/60 mx-1"></div>
          
          <Button
            onClick={openVirtualConsultation}
            variant="outline"
            className="border-2 border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white text-[10px] lg:text-xs px-2 lg:px-3 py-1.5 rounded-full flex items-center gap-1 transition-all duration-200 whitespace-nowrap"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="nav-virtual-consultation"
          >
            <Video className="w-3 h-3" />
            <span>{language === "fr" ? "Virtuel" : "Virtual"}</span>
          </Button>

          {/* Crown Reserve pill removed Feb 24 2026 — was duplicating the
              floating Crown Reserve card on the homepage hero. Frees room
              in the top action row for the Virtual + Referral CTAs. */}

          <Button
            onClick={() => {
              window.location.href = "/referral";
            }}
            className="text-[10px] lg:text-xs px-2 lg:px-3 py-1.5 rounded-full flex items-center gap-1 transition-all duration-200 bg-white hover:bg-[#EEDEC0] text-[#8A6318] hover:text-[#8A6318] border-2 border-[#B8862B] hover:border-[#B8862B] font-semibold whitespace-nowrap shadow-sm"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="nav-get-25"
          >
            <Gift className="w-3 h-3" />
            <span className="hidden xl:inline">{language === "fr" ? "Référer" : "Refer"}</span>
            <span className="font-bold">$50</span>
          </Button>
          </div>
      </div>

      {/* Mobile Menu */}
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {isMobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border p-4 animate-fade-in max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
            {/* Sep 28 2026 · Mobile Nav Trio · matches the desktop
                Promotions/Treatments/Full Menu hero pills so the phone
                first-impression mirrors the browser one. Each pill fires
                the same route/handler as its desktop counterpart. */}
            <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-stone-200" data-testid="mobile-nav-trio">
              <Link
                to="/promotions"
                onClick={() => { setIsMobileMenuOpen(false); setMobileOpenDropdown(null); }}
                data-testid="mobile-nav-promotions-pill"
                className="nav-hot-pulse relative rounded-full border-2 border-red-600 bg-gradient-to-b from-red-500 via-red-600 to-red-700 text-white font-extrabold text-[10.5px] uppercase tracking-[0.06em] py-2.5 px-2 flex items-center justify-center gap-1.5 shadow-[0_3px_10px_-3px_rgba(220,38,38,0.5)] active:scale-[0.97] transition-transform"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <Tag className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shadow-[0_0_6px_rgba(255,255,255,0.9)]"
                />
                <span>{language === "fr" ? "Promos" : "Promos"}</span>
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setMobileOpenDropdown(null);
                  setMobileTreatmentsOpen(true);
                }}
                data-testid="mobile-nav-treatments-pill"
                className="rounded-full border-2 border-amber-500 bg-gradient-to-b from-amber-50 via-amber-100/80 to-amber-50 text-amber-900 font-extrabold text-[10.5px] uppercase tracking-[0.06em] py-2.5 px-2 flex items-center justify-center gap-1 shadow-[0_3px_10px_-3px_rgba(180,140,60,0.45)] active:scale-[0.97] transition-transform"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <span className="text-amber-700">✦</span>
                <span>{language === "fr" ? "Soins" : "Treatments"}</span>
              </button>
              <Link
                to="/book/menu"
                onClick={() => { setIsMobileMenuOpen(false); setMobileOpenDropdown(null); }}
                data-testid="mobile-nav-full-menu-pill"
                className="rounded-full border-2 border-slate-800 bg-white text-slate-900 font-extrabold text-[10.5px] uppercase tracking-[0.06em] py-2.5 px-2 flex items-center justify-center gap-1.5 shadow-[0_3px_10px_-3px_rgba(15,23,42,0.35)] active:scale-[0.97] transition-transform"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span>{language === "fr" ? "Menu" : "Full Menu"}</span>
              </Link>
            </div>
            <Link
              to="/book/free-consultation"
              onClick={() => { setIsMobileMenuOpen(false); setMobileOpenDropdown(null); }}
              data-testid="mobile-nav-free-consult-pill"
              className="nav-hot-pulse mb-3 w-full rounded-full border-2 border-emerald-700 bg-gradient-to-b from-emerald-500 via-emerald-600 to-emerald-700 text-white font-extrabold text-xs uppercase tracking-[0.06em] py-3 px-3 flex items-center justify-center gap-2 shadow-[0_3px_12px_-3px_rgba(5,150,105,0.65)] active:scale-[0.97] transition-transform"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
              <span>{language === "fr" ? "Consultation GRATUITE" : "Book FREE Consultation"}</span>
            </Link>

            <div className="flex flex-col space-y-1">
              {navLinks.filter((l) =>
                // Trio already shown as pills above — hide from the row list.
                !l.isFeaturedPromotions && !l.isFullMenu && !l.isFreeConsult && l.dropdownType !== 'megaMenuTreatments'
              ).map((link) => {
                // For mobile, navigate directly to the page instead of showing dropdown
                const mobileDestination = link.mobileHref || link.href || link.sectionId;
                const isExternalOrSection = !mobileDestination || mobileDestination.startsWith('#');
                
                // Map dropdown types to their main pages
                const dropdownToPage = {
                  'megaMenuTreatments': '/treatments',
                  'botoxTreatments': '/botox-ottawa',
                  'fillerTreatments': '/dermal-fillers',
                  'skinBoosters': '/skin-boosters',
                  'services': '/services',
                  'promotions': '/promotions',
                  'about': '/about',
                  'locations': '/locations',
                  'aftercare': '/aftercare',
                  'skinTips': '/skincare-101',
                  'giftCards': '/gift-cards',
                  'financing': '/financing',
                  'beautyHub': '/beauty-hub',
                  'sculptra': '/sculptra'
                };
                
                const targetPage = link.mobileHref || (link.hasDropdown ? (dropdownToPage[link.dropdownType] || link.href) : (link.href || `/#${link.sectionId}`));

                // Treatments tap opens the bottom-sheet instead of routing.
                if (link.dropdownType === 'megaMenuTreatments') {
                  return (
                    <button
                      key={link.id}
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setMobileOpenDropdown(null);
                        setMobileTreatmentsOpen(true);
                      }}
                      className="w-full flex items-center justify-between text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg text-left transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      data-testid={`mobile-nav-${link.id}`}
                    >
                      <span className="flex items-center gap-2">
                        {link.label}
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm">menu</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  );
                }

                return (
                  <Link
                    key={link.id}
                    to={targetPage}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setMobileOpenDropdown(null);
                    }}
                    className="w-full flex items-center justify-between text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg text-left transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    data-testid={`mobile-nav-${link.id}`}
                  >
                    {link.label}
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                );
              })}

              {/* Financing — collapsible: Afterpay + Medicard + Compare */}
              <button
                type="button"
                onClick={() => setMobileFinancingOpen((v) => !v)}
                aria-expanded={mobileFinancingOpen}
                aria-controls="mobile-financing-submenu"
                className="w-full flex items-center justify-between text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg text-left transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="mobile-nav-financing"
              >
                <span className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-600" />
                  {language === "fr" ? "Financement" : "Financing"}
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">
                    0% interest
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${mobileFinancingOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileFinancingOpen && (
                <div
                  id="mobile-financing-submenu"
                  className="ml-3 pl-3 border-l border-amber-200/70 flex flex-col"
                >
                  <Link
                    to="/afterpay"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setMobileFinancingOpen(false);
                    }}
                    className="flex items-center justify-between text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-2.5 px-2 rounded-lg"
                    data-testid="mobile-nav-financing-afterpay"
                  >
                    <span className="flex items-center gap-2">
                      <span className="bg-[#b2fce4] text-black text-[10px] font-bold px-2 py-0.5 rounded tracking-wide">
                        afterpay
                      </span>
                      <span>{language === "fr" ? "Payez en 4" : "Pay in 4"}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                  <a
                    href="https://apply.medicard.com/22470"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setMobileFinancingOpen(false);
                    }}
                    className="flex items-center justify-between text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-2.5 px-2 rounded-lg"
                    data-testid="mobile-nav-financing-medicard"
                  >
                    <span className="flex items-center gap-2">
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded tracking-wide">
                        Medicard
                      </span>
                      <span>
                        {language === "fr" ? "Demander Medicard" : "Apply for Medicard"}
                      </span>
                    </span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                  <Link
                    to="/financing"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setMobileFinancingOpen(false);
                    }}
                    className="flex items-center justify-between text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-sm font-semibold py-2.5 px-2 rounded-lg"
                    data-testid="mobile-nav-financing-compare"
                  >
                    <span className="flex items-center gap-2">
                      <LayoutGrid className="w-3.5 h-3.5 text-amber-600" />
                      <span>
                        {language === "fr" ? "Comparer toutes les options" : "Compare All Options"}
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                  </Link>
                </div>
              )}

              {/* Refer & Earn $50 — dedicated mobile tab. Routes to /refer
                  where the user lands directly on the share screen with
                  their personalized code (Crown Referral Rewards). */}
              <Link
                to="/refer"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setMobileOpenDropdown(null);
                }}
                className="w-full flex items-center justify-between text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 text-sm py-3 px-2 rounded-lg text-left transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="mobile-nav-refer-earn"
              >
                <span className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-600" />
                  {language === "fr" ? "Recommander & Gagner" : "Refer & Earn"}
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">
                    $50
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 text-emerald-400" />
              </Link>

              {/* Sign Up & Get $60 — dedicated mobile tab. Routes to the
                  client portal where new clients claim 1,000 Crown Points
                  ($60 credit) on signup. */}
              <Link
                to="/my-appointments"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setMobileOpenDropdown(null);
                }}
                className="w-full flex items-center justify-between text-amber-800 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300/70 hover:bg-amber-100 text-sm py-3 px-2 rounded-lg text-left transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="mobile-nav-signup-60"
              >
                <span className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  {language === "fr" ? "Inscrivez-vous — Obtenez" : "Sign Up — Get"}
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-400 text-black rounded-sm">
                    $60 {language === "fr" ? "CRÉDIT" : "CREDIT"}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 text-amber-400" />
              </Link>
              
              {/* Quick Links Section */}
              <div className="pt-4 mt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Quick Links</p>
                <Link 
                  to="/crown-reserve-hub" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-sm py-3 px-2 rounded-lg font-semibold"
                  data-testid="mobile-nav-crown-reserve"
                >
                  <Crown className="w-4 h-4 text-amber-500" />
                  {language === "fr" ? "Crown Reserve — Points & Plans" : "Crown Reserve — Points & Plans"}
                </Link>
                <Link 
                  to="/my-appointments" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg"
                >
                  <User className="w-4 h-4" />
                  {language === "fr" ? "Mon Compte" : "My Account"}
                </Link>
                <Link 
                  to="/virtual-consultation-guide" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg"
                >
                  <Video className="w-4 h-4" />
                  {language === "fr" ? "Consultation Virtuelle" : "Virtual Consultation"}
                </Link>
                <Link
                  to="/professionals"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-sm py-3 px-2 rounded-lg font-semibold"
                  data-testid="mobile-nav-pro-partners"
                >
                  <Briefcase className="w-4 h-4 text-amber-500" />
                  {language === "fr" ? "Programme Pro Partenaires" : "Pro Partner Program"}
                </Link>
                <a 
                  href="https://m.me/PreciseRNInjectionist" onClick={(e) => { e.preventDefault(); openContactModal({ source: "m-me-link" }); }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-700 hover:text-black hover:bg-gray-50 text-sm py-3 px-2 rounded-lg"
                >
                  <MessageCircle className="w-4 h-4" />
                  {language === "fr" ? "Messagez-nous" : "Message Us"}
                </a>
                <a
                  href="/signage"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-menu-tv-signage"
                  className="flex items-center justify-center gap-2 text-zinc-900 bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 border border-amber-400 text-sm font-semibold py-3 px-3 rounded-lg mt-3"
                >
                  <Tv className="w-4 h-4 text-amber-700" />
                  {language === "fr" ? "Logo Écran de Veille" : "Screensaver Logo"}
                </a>
                <a
                  href="/signage/animated"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-menu-tv-signage-animated"
                  className="flex items-center justify-center gap-2 text-amber-50 bg-gradient-to-r from-zinc-900 via-amber-950 to-zinc-900 hover:from-zinc-800 hover:via-amber-900 hover:to-zinc-800 border border-amber-500/60 text-sm font-semibold py-3 px-3 rounded-lg mt-2 shadow-[0_0_20px_rgba(255,196,120,0.18)]"
                >
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  {language === "fr" ? "Logo Animé (Luxe)" : "Screensaver Logo — Animated"}
                </a>
                <a 
                  href="/staff/login"
                  data-testid="mobile-menu-team-signin"
                  className="flex items-center justify-center gap-2 text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-sm font-medium py-3 px-3 rounded-lg mt-3"
                >
                  <Shield className="w-4 h-4" />
                  {language === "fr" ? "Connexion équipe" : "Staff sign-in"}
                </a>
                <button
                  type="button"
                  onClick={() => { toggleLanguage(); setIsMobileMenuOpen(false); }}
                  data-testid="mobile-menu-lang-toggle"
                  className="flex items-center justify-center gap-2 text-gray-800 hover:text-black bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm font-medium py-3 px-3 rounded-lg mt-2"
                >
                  <Globe className="w-4 h-4" />
                  {language === "fr" ? "Switch to English" : "Passer au français"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        scrollToSection={scrollToSection}
      />

      {/* Mobile Bottom Navigation Bar - Hidden on Skin Boosters page (has its own) */}
      {!isOnSkinBoostersPage && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around py-1.5 px-1 max-w-md mx-auto">
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              // On any non-home page (e.g. /last-minute-club, /message-us,
              // /faq, /booking-flow), scrollToSection("hero") is a no-op
              // because the home page's hero section isn't mounted —
              // leaving users stuck unless they tap the logo. Detect the
              // off-home state and navigate to "/" first.
              if (typeof window !== "undefined" && window.location.pathname !== "/") {
                window.location.href = "/";
              } else {
                scrollToSection("hero");
              }
            }}
            className="flex flex-col items-center gap-0.5 px-2 py-1 text-gray-600 hover:text-amber-600 transition-colors"
            data-testid="bottom-nav-home"
          >
            <Home className="w-5 h-5" />
            <span className="text-[10px] font-medium">{language === 'fr' ? 'Accueil' : 'Home'}</span>
          </button>

          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              // The mobile chat widget is mounted via LiveChat at the
              // HomePage level — on other routes the event has nobody to
              // catch it and the tap silently fails. Send users to "/"
              // with a hash that the chat component listens for.
              if (typeof window !== "undefined" && window.location.pathname !== "/") {
                window.location.href = "/#open-chat";
              } else {
                window.dispatchEvent(new CustomEvent('open-mobile-chat'));
              }
            }}
            className="flex flex-col items-center gap-0.5 px-2 py-1 text-gray-600 hover:text-amber-600 transition-colors"
            data-testid="bottom-nav-ask"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-[10px] font-medium">{language === 'fr' ? 'Lumi' : 'Ask Lumi'}</span>
          </button>

          <button
            onClick={() => { window.location.href = "/book/menu"; setIsMobileMenuOpen(false); }}
            className="flex flex-col items-center gap-0.5 px-3 py-1.5 -mt-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full shadow-lg shadow-amber-500/30"
            data-testid="bottom-nav-book"
          >
            <CalendarCheck className="w-5 h-5" />
            <span className="text-[10px] font-bold">{language === 'fr' ? 'Réserver' : 'Book'}</span>
          </button>

          <button
            onClick={() => { window.location.href = "/my-appointments"; setIsMobileMenuOpen(false); }}
            className="relative flex flex-col items-center gap-0.5 px-2 py-1 text-gray-600 hover:text-amber-600 transition-colors"
            data-testid="bottom-nav-account"
          >
            <div className="relative">
              <User className="w-5 h-5" />
              {crownPoints !== null && crownPoints > 0 && (
                <span
                  className="absolute -top-1.5 -right-2 min-w-[20px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-extrabold tabular-nums flex items-center justify-center shadow-md border border-white"
                  data-testid="bottom-nav-account-points-badge"
                  aria-label={`${crownPoints} Crown Points`}
                >
                  {crownPoints > 999 ? `${Math.floor(crownPoints / 1000)}k` : crownPoints}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">
              {crownPoints !== null && crownPoints > 0
                ? `${crownPoints} pts`
                : (language === 'fr' ? 'Compte' : 'Account')}
            </span>
          </button>

          {/* Crown button — auto-pulses every ~45s with a tooltip to remind users */}
          <div className="relative">
            {crownTooltipOpen && (
              <div
                className="absolute -top-12 right-0 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] font-semibold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-in slide-in-from-bottom-2 fade-in"
                data-testid="bottom-nav-crown-tooltip"
              >
                {language === 'fr' ? 'Gagnez des récompenses' : 'Earn rewards →'}
                <div className="absolute -bottom-1 right-4 w-2 h-2 bg-amber-600 rotate-45"></div>
              </div>
            )}
            <button
              onClick={() => { window.location.href = "/crown-reserve-hub"; setIsMobileMenuOpen(false); }}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 text-amber-600 hover:text-amber-700 transition-all ${crownTooltipOpen ? 'animate-bounce' : ''}`}
              data-testid="bottom-nav-rewards"
            >
              <Crown className={`w-5 h-5 ${crownTooltipOpen ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : ''}`} />
              <span className="text-[10px] font-medium">{language === 'fr' ? 'Récompenses' : 'Rewards'}</span>
            </button>
          </div>
        </div>
      </div>
      )}
    </nav>
      <MobileTreatmentsSheet
        open={mobileTreatmentsOpen}
        onClose={() => setMobileTreatmentsOpen(false)}
        language={language}
      />
    </>
  );
};

export default Navbar;
